import PlatformFinance from "@/components/platform/PlatformFinance";
import PlatformRecords from "@/components/platform/PlatformRecords";
import BillingDiagnostics from "@/components/platform/BillingDiagnostics";
import { useSearchParams } from "react-router-dom";
import { PlatformOverview, CRMRegister, CustomerSuccess, Pager } from "@/components/platform/PlatformCRM";
import CustomerCampaigns from "@/components/platform/CustomerCampaigns";
import { PLATFORM_GROUPS } from "@/components/platform/PlatformShell";
import { clientVersion } from "@/lib/clientInfo";
import { useEffect, useMemo, useState, useRef } from "react";
import api from "@/lib/api";
import { PageHeader } from "./_shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Building2, Users, CreditCard, BarChart3, ShieldCheck, LifeBuoy, Megaphone,
  Search, KeyRound, AlertTriangle, Trash2, Send, Copy, ExternalLink, Power,
  PowerOff, Activity, FlagTriangleRight, DollarSign, X, ChevronRight,
  Film, Download, Play, RefreshCw, Sparkles, Image, TrendingUp, MapPin, Smartphone, Monitor, Tablet, LogIn, Database, BellRing, Mail, Lightbulb, GraduationCap, RotateCcw, Bird, Compass, MessageSquarePlus,
} from "lucide-react";
import { toast } from "sonner";
import TrafficPanel from "./TrafficPanel";
import WinBackPanel from "./WinBackPanel";
import EmailPreviewPanel from "./EmailPreviewPanel";
import CampaignsPanel from "./CampaignsPanel";
import OnboardingPanel from "./OnboardingPanel";
import FeatureRequestsPanel from "./FeatureRequestsPanel";
import HaveYourSayPanel from "./HaveYourSayPanel";
import { BusinessProfilesPanel } from "@/components/BusinessProfilesPanel";
import { KiriControlPanel } from "@/components/KiriControlPanel";
import { OnboardingControlPanel } from "@/components/OnboardingControlPanel";
import CommandCentre from "./CommandCentre";

// Force a cross-origin file download. The HTML `download` attribute is
// silently ignored by browsers when the URL is cross-origin without an
// `Content-Disposition: attachment` header (and iOS Safari PWA blocks it
// outright). We fetch the bytes with the Authorization token, build a blob
// URL, and synthesize an anchor click so the file always saves with the
// correct filename.
async function downloadFileAsBlob(url, filename) {
  try {
    const tok = typeof window !== "undefined" ? localStorage.getItem("fl_token") || "" : "";
    // Append download=1 so the server also returns Content-Disposition.
    const u = new URL(url, window.location.origin);
    if (!u.searchParams.has("download")) u.searchParams.set("download", "1");
    const res = await fetch(u.toString(), {
      headers: tok ? { Authorization: `Bearer ${tok}` } : {},
      credentials: "omit",
    });
    if (!res.ok) {
      throw new Error(`HTTP ${res.status}`);
    }
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = filename || "download";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(blobUrl), 1500);
  } catch (e) {
    toast.error(`Download failed: ${e.message || e}`);
  }
}

const TABS = [
  { key: "overview", label: "Overview", icon: BarChart3 },
  { key: "traffic", label: "Site traffic", icon: TrendingUp },
  { key: "orgs", label: "Organisations", icon: Building2 },
  { key: "chase", label: "Chase-ups", icon: BellRing },
  { key: "winback", label: "Win-Back", icon: RotateCcw },
  { key: "emails", label: "Emails", icon: Mail },
  { key: "features", label: "Feature requests", icon: Lightbulb },
  { key: "have-your-say", label: "Have your say", icon: MessageSquarePlus },
  { key: "crashes", label: "Crashes", icon: AlertTriangle },
  { key: "users", label: "Users", icon: Users },
  { key: "billing", label: "Billing", icon: CreditCard },
  { key: "analytics", label: "Analytics", icon: Activity },
  { key: "audit", label: "Audit log", icon: ShieldCheck },
  { key: "support-sessions", label: "Support sessions", icon: LogIn },
  { key: "support", label: "Support", icon: LifeBuoy },
  { key: "broadcast", label: "Broadcast", icon: Megaphone },
  { key: "campaigns", label: "Campaigns", icon: Send },
  { key: "onboarding", label: "Onboarding", icon: GraduationCap },
  { key: "guided-onboarding", label: "Guided Onboarding", icon: Compass },
  { key: "ads", label: "AI Ads", icon: Film },
  { key: "brand", label: "Brand", icon: Image },
  { key: "biz-profiles", label: "Business Profiles", icon: Building2 },
  { key: "kiri", label: "Kiri", icon: Bird },
  { key: "system", label: "System", icon: Database },
];

// ============================================================
//  CHASE-UPS — orgs gone quiet; send a re-engagement email
// ============================================================
function ChasePanel() {
  const [days, setDays] = useState(14);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState({});

  const load = async (d = days) => {
    setLoading(true);
    try {
      const { data } = await api.get("/platform/inactive", { params: { days: d } });
      setItems(data.items || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Load failed"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const chase = async (org) => {
    setSending((s) => ({ ...s, [org.id]: true }));
    try {
      const { data } = await api.post(`/platform/orgs/${org.id}/chase`, { message: message.trim() || null });
      toast.success(`Chase-up sent to ${data.sent_to}`);
      setItems((arr) => arr.map((o) => o.id === org.id ? { ...o, last_chase_email_at: new Date().toISOString() } : o));
    } catch (e) { toast.error(e.response?.data?.detail || "Send failed"); }
    finally { setSending((s) => ({ ...s, [org.id]: false })); }
  };

  return (
    <div className="space-y-4" data-testid="chase-panel">
      <div className="hard-card p-4 flex flex-wrap items-end gap-4">
        <div>
          <div className="label-tag mb-2">Inactive for at least</div>
          <div className="flex gap-1">
            {[7, 14, 30, 60].map((d) => (
              <button key={d} onClick={() => { setDays(d); load(d); }}
                      className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider border-2 rounded-sm ${days === d ? "border-[#FF5F15] bg-[#FF5F15] text-white" : "border-zinc-300 text-zinc-600 hover:border-zinc-400"}`}
                      data-testid={`chase-days-${d}`}>
                {d} days
              </button>
            ))}
          </div>
        </div>
        <div className="flex-1 min-w-[260px]">
          <div className="label-tag mb-2">Personal note (optional — goes in every chase-up you send below)</div>
          <input value={message} onChange={(e) => setMessage(e.target.value)}
                 placeholder="e.g. Saw you signed up last week — keen to give you a quick walkthrough?"
                 className="w-full h-9 px-3 text-sm border border-zinc-400 rounded-sm bg-transparent"
                 data-testid="chase-message" />
        </div>
        <div className="text-xs font-mono text-zinc-500 pb-2" data-testid="chase-count">
          {loading ? "Loading…" : `${items.length} quiet org${items.length === 1 ? "" : "s"}`}
        </div>
      </div>

      {!loading && items.length === 0 && (
        <div className="hard-card p-10 text-center text-sm text-zinc-500">
          No orgs have gone quiet for {days}+ days. Everyone's on the tools 🎉
        </div>
      )}

      <div className="space-y-2">
        {items.map((o) => (
          <div key={o.id} className="hard-card p-4 flex flex-wrap items-center gap-3" data-testid={`chase-row-${o.id}`}>
            <div className="min-w-0 flex-1">
              <div className="font-bold text-sm">{o.name}</div>
              <div className="text-[11px] font-mono text-zinc-500 mt-0.5">
                {o.contact_email || "no contact email"} · {o.users_count} user{o.users_count === 1 ? "" : "s"} · plan {o.plan || "—"}
              </div>
            </div>
            <div className="text-right shrink-0">
              <div className="text-sm font-bold text-red-600">{o.days_inactive != null ? `${o.days_inactive}d quiet` : "never logged in"}</div>
              <div className="text-[10px] font-mono text-zinc-400">
                {o.last_login_at ? `last login ${fmtDate(o.last_login_at)}` : `signed up ${fmtDate(o.created_at)}`}
              </div>
              {o.last_chase_email_at && (
                <div className="text-[10px] font-mono text-amber-600">chased {fmtDate(o.last_chase_email_at)}</div>
              )}
            </div>
            <button onClick={() => chase(o)} disabled={!o.contact_email || sending[o.id]}
                    className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 bg-[#FF5F15] hover:bg-[#E65100] disabled:opacity-40 text-white text-[10px] font-bold uppercase tracking-wider rounded-sm"
                    data-testid={`chase-send-${o.id}`}>
              <Mail className="w-3.5 h-3.5" /> {sending[o.id] ? "Sending…" : o.last_chase_email_at ? "Chase again" : "Send chase-up"}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================================
//  CRASHES — client-side crash reports (auto-captured)
// ============================================================
function CrashesPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/client-errors")
      .then(({ data }) => setItems(data || []))
      .catch(() => toast.error("Failed to load crash reports"))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="space-y-3" data-testid="crashes-panel">
      <div className="text-xs font-mono text-zinc-500">
        {loading ? "Loading…" : `${items.length} crash report${items.length === 1 ? "" : "s"} — captured automatically from user devices`}
      </div>
      {!loading && items.length === 0 && (
        <div className="hard-card p-10 text-center text-sm text-zinc-500">No crashes reported. Smooth sailing 🎉</div>
      )}
      {items.map((e) => (
        <div key={e.id} className="hard-card p-4" data-testid={`crash-row-${e.id}`}>
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <span className="font-bold text-red-600 uppercase">{e.kind}</span>
            <span className="font-mono text-zinc-500">{fmtDate(e.created_at)} {String(e.created_at || "").slice(11, 16)}</span>
            <span className="font-mono text-zinc-400 truncate max-w-[40%]">{e.url}</span>
          </div>
          <div className="text-sm font-bold mt-1 break-words">{e.message || "(no message)"}</div>
          <div className="text-[10px] font-mono text-zinc-500 mt-1 break-all">{e.user_agent}</div>
          {e.stack && (
            <details className="mt-1 text-[10px] font-mono text-zinc-500">
              <summary className="cursor-pointer select-none">Stack trace</summary>
              <pre className="whitespace-pre-wrap break-all mt-1">{e.stack}</pre>
            </details>
          )}
        </div>
      ))}
    </div>
  );
}

function bytes(n) {
  if (!n) return "0 B";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}
function fmtDate(iso) { return iso ? new Date(iso).toLocaleString("en-NZ") : "—"; }

// Visual badge for a trial: green count-down, yellow when ≤3 days, red when expired.
// Shows "—" when the org isn't on a trial plan.
function TrialBadge({ org }) {
  if (org.plan_status !== "trial") {
    return <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-400">—</span>;
  }
  const days = org.trial_days_left;
  if (days == null) {
    return <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-400">no end date</span>;
  }
  if (days < 0) {
    return (
      <span className="inline-flex items-center gap-1 bg-red-600 text-white text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm" data-testid="trial-badge-expired">
        Expired · {Math.abs(days)}d ago
      </span>
    );
  }
  if (days <= 3) {
    return (
      <span className="inline-flex items-center gap-1 bg-yellow-100 text-yellow-900 border border-yellow-400 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm" data-testid="trial-badge-soon">
        {days === 0 ? "Ends today" : `${days}d left`}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 bg-blue-100 text-blue-800 border border-blue-300 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm" data-testid="trial-badge-active">
      {days}d left
    </span>
  );
}
function fmtDay(iso) { return iso ? new Date(iso).toLocaleDateString("en-NZ", { day: "numeric", month: "short", year: "numeric" }) : "—"; }
function fmtMoney(n) { return `NZ$${Number(n || 0).toLocaleString("en-NZ", { minimumFractionDigits: 2 })}`; }

function MaintenanceControl() {
  const [state, setState] = useState(null);
  const [msg, setMsg] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/platform/maintenance");
      setState(data);
      setMsg(data.message || "");
    } catch {
      toast.error("Failed to load maintenance status");
    }
  };
  useEffect(() => { load(); }, []);

  const apply = async (active) => {
    setSaving(true);
    try {
      const { data } = await api.put("/platform/maintenance", { active, message: msg });
      setState((s) => ({ ...(s || {}), active: data.active, message: data.message }));
      toast.success(active
        ? "Maintenance mode ON — only super admins can use the app"
        : "Maintenance mode OFF — the app is live for everyone");
    } catch {
      toast.error("Could not update maintenance mode");
    } finally {
      setSaving(false);
    }
  };

  if (!state) return <div className="text-sm font-mono text-zinc-500">Loading maintenance control…</div>;
  const on = !!state.active;

  return (
    <div className={`hard-card p-5 border-l-4 ${on ? "border-red-600 bg-red-50" : "border-zinc-300 bg-white"}`} data-testid="maintenance-control">
      <div className="flex flex-wrap items-center gap-4">
        <div className={`inline-flex items-center justify-center h-11 w-11 ${on ? "bg-red-600" : "bg-zinc-900"}`}>
          {on ? <PowerOff className="w-5 h-5 text-white" /> : <Power className="w-5 h-5 text-white" />}
        </div>
        <div className="flex-1 min-w-[220px]">
          <div className="font-display font-black uppercase tracking-tight text-lg flex items-center gap-2">
            Maintenance mode
            <span data-testid="maintenance-status-badge"
              className={`text-[10px] font-bold px-2 py-0.5 ${on ? "bg-red-600 text-white" : "bg-emerald-600 text-white"}`}>
              {on ? "ON — CUSTOMERS LOCKED OUT" : "OFF — LIVE"}
            </span>
          </div>
          <div className="text-sm text-zinc-600 mt-0.5">
            When on, everyone except super admins sees a “we’ll be right back” page and cannot sign in or use the app.
          </div>
          {state.updated_at && (
            <div className="text-[11px] font-mono text-zinc-400 mt-1">
              Last changed {new Date(state.updated_at).toLocaleString("en-NZ")}{state.updated_by ? ` · ${state.updated_by}` : ""}
            </div>
          )}
        </div>
      </div>

      <div className="mt-4">
        <Label className="label-tag">Message shown to customers</Label>
        <Textarea value={msg} onChange={(e) => setMsg(e.target.value)} rows={3} className="mt-2"
          data-testid="maintenance-message-input"
          placeholder="Sorry, we're currently doing some maintenance to the app and will be back up and running shortly." />
      </div>

      <div className="mt-4 flex flex-wrap gap-3">
        {on ? (
          <>
            <Button onClick={() => apply(false)} disabled={saving} data-testid="maintenance-toggle-off"
              className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm">
              <Power className="w-4 h-4 mr-1.5" /> Turn maintenance OFF
            </Button>
            <Button variant="outline" onClick={() => apply(true)} disabled={saving} data-testid="maintenance-save-message"
              className="rounded-sm border-zinc-400">
              Save message
            </Button>
          </>
        ) : (
          <Button onClick={() => apply(true)} disabled={saving} data-testid="maintenance-toggle-on"
            className="bg-red-600 hover:bg-red-700 text-white rounded-sm">
            <PowerOff className="w-4 h-4 mr-1.5" /> Turn maintenance ON
          </Button>
        )}
      </div>
    </div>
  );
}

function XeroAuditPurge() {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const purge = async (keep_days) => {
    setBusy(true);
    try {
      const { data } = await api.post("/platform/purge-xero-audit", { keep_days });
      setResult(data);
      toast.success(`Cleared ${data.deleted.toLocaleString()} Xero sync log rows`);
      if (!data.space_reclaimed) {
        toast.message("Rows deleted, but the hosting tier blocked instant disk reclaim — space frees up as data is overwritten.");
      }
    } catch {
      toast.error("Could not clear the Xero sync log");
    } finally {
      setBusy(false);
      setOpen(false);
    }
  };

  return (
    <div className="hard-card p-5 border-l-4 border-amber-500 bg-amber-50" data-testid="xero-audit-purge">
      <div className="flex flex-wrap items-center gap-4">
        <div className="inline-flex items-center justify-center h-11 w-11 bg-amber-500">
          <Trash2 className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-[220px]">
          <div className="font-display font-black uppercase tracking-tight text-lg">Clear Xero sync log</div>
          <div className="text-sm text-zinc-600 mt-0.5">
            The Xero sync audit log is the biggest thing filling the database. Clearing it frees space immediately and is safe — it only stores a history of sync activity and rebuilds itself. It does not affect any invoices, payments or sync behaviour.
          </div>
          {result && (
            <div className="text-[11px] font-mono text-zinc-500 mt-1" data-testid="xero-audit-purge-result">
              Last run: removed {Number(result.deleted).toLocaleString()} rows ({Number(result.before).toLocaleString()} → {Number(result.after).toLocaleString()}).
            </div>
          )}
        </div>
        <Button onClick={() => setOpen(true)} className="bg-amber-600 hover:bg-amber-700 text-white rounded-sm" data-testid="xero-audit-purge-open">
          <Trash2 className="w-4 h-4 mr-1.5" /> Clear sync log
        </Button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="xero-audit-purge-dialog">
          <DialogHeader>
            <DialogTitle>Clear the Xero sync log?</DialogTitle>
            <DialogDescription>
              This permanently deletes the Xero sync activity history to free database space. Your invoices, payments and Xero connection are not affected, and the log starts fresh. Choose how much to keep.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" disabled={busy} onClick={() => purge(7)} className="rounded-sm" data-testid="xero-audit-purge-keep7">
              Keep last 7 days
            </Button>
            <Button disabled={busy} onClick={() => purge(0)} className="bg-amber-600 hover:bg-amber-700 text-white rounded-sm" data-testid="xero-audit-purge-all">
              {busy ? "Clearing…" : "Clear everything & reclaim space"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// Super-admin seasonal theme switch (e.g. Halloween). Applies to everyone.
const SEASONAL_THEMES = [
  { key: "none", label: "Default", emoji: "", blurb: "Standard Fieldlink branding." },
  { key: "halloween", label: "Halloween", emoji: "🎃", blurb: "Spooky pumpkin & purple theme with bats and cobwebs." },
];

function SeasonalThemeControl() {
  const [theme, setTheme] = useState(null);
  const [saving, setSaving] = useState(null);

  useEffect(() => {
    api.get("/platform/theme").then((r) => setTheme(r.data?.theme || "none")).catch(() => setTheme("none"));
  }, []);

  const choose = async (key) => {
    if (key === theme) return;
    setSaving(key);
    try {
      await api.put("/platform/theme", { theme: key });
      setTheme(key);
      window.dispatchEvent(new CustomEvent("fieldlink:theme", { detail: { theme: key } }));
      toast.success(key === "none" ? "Theme reset to default" : `${SEASONAL_THEMES.find((t) => t.key === key)?.label} theme is live for everyone`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't change theme");
    } finally { setSaving(null); }
  };

  return (
    <div className="hard-card p-5" data-testid="seasonal-theme-control">
      <div className="flex items-center gap-3 mb-1">
        <div className="inline-flex items-center justify-center h-11 w-11 bg-[#FF5F15]"><Sparkles className="w-5 h-5 text-white" /></div>
        <div>
          <div className="font-display font-black uppercase tracking-tight text-lg">Seasonal theme</div>
          <div className="text-sm text-zinc-600">Switch a fun site-wide theme on or off. Everyone sees it instantly.</div>
        </div>
      </div>
      <div className="mt-4 grid sm:grid-cols-2 gap-3">
        {SEASONAL_THEMES.map((t) => {
          const active = theme === t.key;
          return (
            <button key={t.key} onClick={() => choose(t.key)} disabled={saving !== null || theme === null}
              data-testid={`theme-option-${t.key}`}
              className={`text-left rounded-lg border p-4 transition-colors ${active ? "border-[#FF5F15] bg-orange-50 dark:bg-orange-950/40" : "border-zinc-200 dark:border-white/10 hover:border-[#FF5F15]"}`}>
              <div className="flex items-center justify-between">
                <span className="font-bold">{t.emoji} {t.label}</span>
                {active && <span className="text-[10px] font-bold uppercase tracking-wider text-[#FF5F15]">{saving === t.key ? "Saving…" : "Live"}</span>}
              </div>
              <div className="text-xs text-zinc-500 mt-1">{t.blurb}</div>
            </button>
          );
        })}
      </div>
    </div>
  );
}


// Account deletion requests (Google Play compliance). Field workers and the
// public can request deletion; the team verifies and actions them here.
const DELETION_STATUS = {
  pending: { label: "Pending", cls: "bg-amber-100 text-amber-900 border-amber-400" },
  in_progress: { label: "In progress", cls: "bg-blue-100 text-blue-900 border-blue-400" },
  completed: { label: "Completed", cls: "bg-green-100 text-green-900 border-green-400" },
  rejected: { label: "Rejected", cls: "bg-zinc-200 text-zinc-800 border-zinc-400" },
};

function DeletionRequestsPanel() {
  const [rows, setRows] = useState(null);
  const [savingId, setSavingId] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/platform/account-deletion-requests");
      setRows(data?.requests || []);
    } catch { setRows([]); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const setStatus = async (id, status) => {
    setSavingId(id);
    try {
      await api.patch(`/platform/account-deletion-requests/${id}`, { status });
      setRows((rs) => rs.map((r) => (r.id === id ? { ...r, status } : r)));
      toast.success(`Marked ${DELETION_STATUS[status]?.label || status}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update status");
    } finally { setSavingId(null); }
  };

  const pending = (rows || []).filter((r) => r.status === "pending").length;

  return (
    <div className="hard-card p-5 border-l-4 border-red-500 bg-red-50" data-testid="deletion-requests-panel">
      <div className="flex flex-wrap items-center gap-4">
        <div className="inline-flex items-center justify-center h-11 w-11 bg-red-600">
          <Trash2 className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-[220px]">
          <div className="font-display font-black uppercase tracking-tight text-lg">Account deletion requests</div>
          <div className="text-sm text-zinc-600 mt-0.5">
            Users’ requests to delete their account &amp; personal data (Google Play requirement).
            {pending > 0 && <> <b className="text-red-700">{pending} pending</b>.</>}
          </div>
        </div>
        <Button variant="outline" onClick={load} className="rounded-sm" data-testid="deletion-requests-refresh">
          <RefreshCw className="w-4 h-4 mr-1.5" /> Refresh
        </Button>
      </div>

      <div className="mt-4 space-y-2" data-testid="deletion-requests-list">
        {rows === null ? (
          <div className="text-sm text-zinc-500">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="text-sm text-zinc-500" data-testid="deletion-requests-empty">No deletion requests.</div>
        ) : rows.map((r) => {
          const st = DELETION_STATUS[r.status] || DELETION_STATUS.pending;
          return (
            <div key={r.id} className="bg-white border border-zinc-200 p-3 flex flex-wrap items-center gap-3" data-testid={`deletion-request-${r.id}`}>
              <div className="flex-1 min-w-[220px]">
                <div className="font-bold text-sm">{r.name || "—"} <span className="font-mono font-normal text-zinc-500">· {r.email}</span></div>
                <div className="text-[11px] text-zinc-500">
                  {r.organisation || "No organisation"} · {r.source === "in_app" ? "In-app" : "Public form"}
                  {r.user_id ? " · verified user" : ""} · {new Date(r.created_at).toLocaleString("en-NZ")}
                </div>
                {r.reason && <div className="text-xs text-zinc-600 mt-1 italic">“{r.reason}”</div>}
              </div>
              <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 border rounded-sm ${st.cls}`}>{st.label}</span>
              <Select value={r.status} onValueChange={(v) => setStatus(r.id, v)} disabled={savingId === r.id}>
                <SelectTrigger className="w-[150px] h-9 rounded-sm" data-testid={`deletion-status-${r.id}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(DELETION_STATUS).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function StorageMigration() {
  const [st, setSt] = useState(null);
  const [starting, setStarting] = useState(false);
  const timer = useRef(null);

  const load = async () => {
    try { const { data } = await api.get("/platform/migrate-storage"); setSt(data); return data; }
    catch { return null; }
  };
  useEffect(() => {
    load();
    return () => { if (timer.current) clearInterval(timer.current); };
  }, []);
  useEffect(() => {
    if (st?.running && !timer.current) {
      timer.current = setInterval(async () => {
        const d = await load();
        if (d && !d.running && timer.current) { clearInterval(timer.current); timer.current = null; }
      }, 3000);
    }
  }, [st?.running]);

  const start = async () => {
    setStarting(true);
    try {
      await api.post("/platform/migrate-storage");
      toast.success("Migration started — copying your files to your storage");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start migration");
    } finally { setStarting(false); }
  };

  if (!st) return null;
  const connected = st.r2_enabled;
  const total = st.total || 0;
  const done = st.done || 0;
  const pct = total ? Math.round((done / total) * 100) : 0;

  return (
    <div className="hard-card p-5 border-l-4 border-sky-500 bg-sky-50" data-testid="storage-migration">
      <div className="flex flex-wrap items-center gap-4">
        <div className="inline-flex items-center justify-center h-11 w-11 bg-sky-600">
          <Database className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-[220px]">
          <div className="font-display font-black uppercase tracking-tight text-lg flex items-center gap-2">
            Your own file storage
            <span data-testid="storage-migration-badge"
              className={`text-[10px] font-bold px-2 py-0.5 ${connected ? "bg-emerald-600 text-white" : "bg-zinc-300 text-zinc-700"}`}>
              {connected ? "CLOUDFLARE R2 CONNECTED" : "NOT CONFIGURED"}
            </span>
          </div>
          <div className="text-sm text-zinc-600 mt-0.5">
            New uploads already go to your bucket. Run this once to copy all existing photos & files across — reads fall back to the old storage until each file is copied, so nothing breaks during migration. Safe to re-run; it skips files already copied.
          </div>
        </div>
        <Button onClick={start} disabled={!connected || starting || st.running}
          className="bg-sky-600 hover:bg-sky-700 text-white rounded-sm" data-testid="storage-migration-start">
          {st.running ? "Copying…" : starting ? "Starting…" : "Copy existing files"}
        </Button>
      </div>

      {(st.running || st.finished_at) && (
        <div className="mt-4" data-testid="storage-migration-progress">
          <div className="flex justify-between text-[11px] font-mono text-zinc-500 mb-1">
            <span>{st.running ? "Copying files…" : "Last migration"}</span>
            <span>{done.toLocaleString()} / {total.toLocaleString()} ({pct}%)</span>
          </div>
          <div className="h-3 bg-white border border-zinc-300 overflow-hidden">
            <div className="h-full bg-sky-500 transition-all" style={{ width: `${pct}%` }} />
          </div>
          <div className="mt-1.5 text-[11px] font-mono text-zinc-500">
            {st.copied?.toLocaleString() || 0} copied · {st.skipped?.toLocaleString() || 0} skipped · {st.failed?.toLocaleString() || 0} failed
            {st.finished_at && !st.running ? ` · finished ${new Date(st.finished_at).toLocaleTimeString("en-NZ")}` : ""}
          </div>
          {st.snapshots_total != null && (
            <div className="mt-1 text-[11px] font-mono text-zinc-500">
              Issued-document PDFs: {Number(st.snapshots_migrated || 0).toLocaleString()} moved · {Number(st.snapshots_skipped || 0).toLocaleString()} already done · {Number(st.snapshots_failed || 0).toLocaleString()} failed
            </div>
          )}
          {st.last_error && <div className="mt-1 text-[11px] font-mono text-red-600">Last error: {st.last_error}</div>}
        </div>
      )}
    </div>
  );
}

function PushTest() {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/platform/test-push");
      setResult(data);
      if (data?.skipped) toast.message("Push sender not configured yet (deploy the Cloud Run sender + set FCM_SENDER_URL)");
      else if (data?.error) toast.error("Push failed: " + data.error);
      else if ((data?.sent || 0) > 0) toast.success(`Sent to ${data.sent} device(s)`);
      else toast.message("No devices registered for your account yet");
    } catch {
      toast.error("Could not send test push");
    } finally { setBusy(false); }
  };
  return (
    <div className="hard-card p-5 border-l-4 border-violet-500 bg-violet-50" data-testid="push-test">
      <div className="flex flex-wrap items-center gap-4">
        <div className="inline-flex items-center justify-center h-11 w-11 bg-violet-600">
          <BellRing className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-[220px]">
          <div className="font-display font-black uppercase tracking-tight text-lg">Push notifications</div>
          <div className="text-sm text-zinc-600 mt-0.5">
            Sends a test notification to your own signed-in devices. Works once the keyless Cloud Run sender is deployed and FCM_SENDER_URL is set.
          </div>
          {result && <div className="text-[11px] font-mono text-zinc-500 mt-1" data-testid="push-test-result">{JSON.stringify(result)}</div>}
        </div>
        <Button onClick={run} disabled={busy} className="bg-violet-600 hover:bg-violet-700 text-white rounded-sm" data-testid="push-test-send">
          <BellRing className="w-4 h-4 mr-1.5" /> {busy ? "Sending…" : "Send test push"}
        </Button>
      </div>
    </div>
  );
}

function SystemHealthPanel() {
  const [h, setH] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/platform/system-health");
      setH(data);
    } catch (e) {
      toast.error("Failed to load system health");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  if (loading && !h) return <div className="text-sm font-mono text-zinc-500">Checking database…</div>;
  if (!h) return null;

  const statusStyles = {
    ok: { bar: "bg-emerald-500", banner: "border-emerald-500 bg-emerald-50", label: "HEALTHY", text: "text-emerald-700" },
    warning: { bar: "bg-amber-500", banner: "border-amber-500 bg-amber-50", label: "UPGRADE SOON", text: "text-amber-700" },
    critical: { bar: "bg-red-600", banner: "border-red-600 bg-red-50", label: "UPGRADE NOW", text: "text-red-700" },
    unknown: { bar: "bg-zinc-400", banner: "border-zinc-400 bg-zinc-50", label: "UNKNOWN", text: "text-zinc-600" },
  };
  const st = statusStyles[h.status] || statusStyles.unknown;

  return (
    <div className="space-y-6" data-testid="system-health-panel">
      <MaintenanceControl />
      <SeasonalThemeControl />
      <StorageMigration />
      <PushTest />
      <XeroAuditPurge />
      <DeletionRequestsPanel />
      <div className={`border-l-4 p-4 flex flex-wrap items-center gap-4 ${st.banner}`} data-testid="system-health-banner">
        <Database className={`w-6 h-6 ${st.text}`} />
        <div className="flex-1 min-w-[220px]">
          <div className={`font-display font-black uppercase tracking-tight text-lg ${st.text}`} data-testid="system-health-status">
            Database: {st.label}
          </div>
          <div className="text-sm text-zinc-700 mt-0.5">{h.advice}</div>
        </div>
        <Button variant="outline" size="sm" onClick={load} className="rounded-sm border-zinc-400" data-testid="system-health-refresh">
          <RefreshCw className="w-3.5 h-3.5 mr-1" /> Re-check
        </Button>
      </div>

      <div className="hard-card bg-white p-5">
        <div className="flex items-baseline justify-between flex-wrap gap-2">
          <div className="label-tag">Database capacity</div>
          <div className="font-mono text-sm font-bold" data-testid="system-usage-pct">
            {h.used_pct != null ? `${h.used_pct}% of ${h.capacity_mb} MB` : "capacity unknown"}
          </div>
        </div>
        <div className="mt-3 h-4 bg-zinc-100 border border-zinc-300 overflow-hidden">
          <div className={`h-full ${st.bar} transition-all`} style={{ width: `${Math.min(100, h.used_pct || 0)}%` }} />
        </div>
        <div className="mt-1.5 flex justify-between text-[10px] font-mono text-zinc-400">
          <span>0</span><span className="text-amber-600">70% — plan upgrade</span><span className="text-red-600">90% — upgrade now</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-5">
          {[
            ["Data", bytes(h.data_size)],
            ["On disk", bytes(h.storage_size)],
            ["Indexes", bytes(h.index_size)],
            ["Documents", (h.objects || 0).toLocaleString()],
            ["File uploads", `${bytes(h.files_total_bytes)} (${h.files_count})`],
          ].map(([k, v]) => (
            <div key={k} className="border border-zinc-200 p-3">
              <div className="text-[9px] font-bold uppercase tracking-widest text-zinc-500">{k}</div>
              <div className="font-display font-bold text-base mt-0.5">{v}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="hard-card bg-white p-5">
          <div className="label-tag mb-3">Biggest collections</div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 border-b border-zinc-200">
                <th className="text-left py-1.5">Collection</th>
                <th className="text-right py-1.5">Docs</th>
                <th className="text-right py-1.5">Size on disk</th>
              </tr>
            </thead>
            <tbody>
              {h.collections.map((c) => (
                <tr key={c.name} className="border-b border-zinc-100" data-testid={`system-coll-${c.name}`}>
                  <td className="py-1.5 font-mono text-xs">{c.name}</td>
                  <td className="py-1.5 text-right font-mono text-xs">{c.count.toLocaleString()}</td>
                  <td className="py-1.5 text-right font-mono text-xs font-bold">{bytes(c.storage)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="hard-card bg-white p-5">
          <div className="label-tag mb-3">Platform records</div>
          <div className="grid grid-cols-2 gap-3">
            {Object.entries(h.counts || {}).map(([k, v]) => (
              <div key={k} className="border border-zinc-200 p-3">
                <div className="text-[9px] font-bold uppercase tracking-widest text-zinc-500">{k.replace("_", " ")}</div>
                <div className="font-display font-bold text-lg mt-0.5">{Number(v).toLocaleString()}</div>
              </div>
            ))}
          </div>
          <div className="mt-4 text-[11px] font-mono text-zinc-500">
            {h.submissions_30d.toLocaleString()} form submissions in the last 30 days ·
            checked {new Date(h.checked_at).toLocaleTimeString("en-NZ")}
          </div>
          <div className="mt-3 text-xs text-zinc-500 border-t border-zinc-200 pt-3">
            When usage passes 70%, contact Emergent support to upgrade the deployment's database tier.
            You can adjust the tracked capacity with the DB_CAPACITY_MB environment variable.
          </div>
        </div>
      </div>
    </div>
  );
}

/* ====== App versions & platforms ====== */
const PLATFORM_LABEL = {
  android: "Android app",
  ios: "iOS app",
  pwa: "Installed web (PWA)",
  web: "Web browser",
};
const PLATFORM_ICON = { android: Smartphone, ios: Smartphone, pwa: Tablet, web: Monitor };

function ClientVersionsPanel() {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");
  const [q, setQ] = useState("");
  const [platformFilter, setPlatformFilter] = useState("");
  const current = clientVersion(); // the version THIS super-admin is running = latest

  useEffect(() => {
    api.get("/platform/client-versions")
      .then((r) => setRows(r.data?.users || []))
      .catch(() => setError("Could not load app versions"));
  }, []);

  const isBehind = (v) => !!current && !!v && v !== current;

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return (rows || []).filter((r) => {
      if (platformFilter && (r.platform || "") !== platformFilter) return false;
      if (!term) return true;
      return [r.name, r.email, r.org_name, r.version, r.role]
        .some((f) => (f || "").toLowerCase().includes(term));
    });
  }, [rows, q, platformFilter]);

  const seen = (rows || []).filter((r) => r.version || r.platform);
  const behindCount = seen.filter((r) => isBehind(r.version)).length;
  const fmtWhen = (t) => {
    if (!t) return "—";
    try { return new Date(t).toLocaleString("en-NZ", { dateStyle: "medium", timeStyle: "short" }); }
    catch { return "—"; }
  };

  if (error) return <div className="pa-error" data-testid="versions-error">{error}</div>;
  if (!rows) return <div className="text-zinc-500">Loading…</div>;

  return (
    <div className="space-y-5" data-testid="platform-versions">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Current release" value={current || "—"} sub="what this admin is on" />
        <StatCard label="Users seen" value={seen.length} sub={`${rows.length} total accounts`} />
        <StatCard label="On the latest" value={seen.length - behindCount} sub="matching current release" />
        <StatCard label="Update pending" value={behindCount} sub="behind the current release"
                  alert={behindCount > 0} danger={behindCount > 0} />
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        <input
          data-testid="versions-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search name, email, company, version…"
          className="flex-1 min-w-[220px] h-10 px-3 text-sm bg-white border border-zinc-300 rounded-sm focus:outline-none focus:border-[#FF5F15]"
        />
        <select
          data-testid="versions-platform-filter"
          value={platformFilter}
          onChange={(e) => setPlatformFilter(e.target.value)}
          className="h-10 px-3 text-sm bg-white border border-zinc-300 rounded-sm"
        >
          <option value="">All platforms</option>
          <option value="android">Android app</option>
          <option value="ios">iOS app</option>
          <option value="pwa">Installed web (PWA)</option>
          <option value="web">Web browser</option>
        </select>
      </div>

      <div className="hard-card overflow-x-auto">
        <table className="w-full text-sm" data-testid="versions-table">
          <thead>
            <tr className="text-left border-b border-zinc-300">
              <th className="px-4 py-3 font-bold uppercase tracking-wider text-xs">User</th>
              <th className="px-4 py-3 font-bold uppercase tracking-wider text-xs">Company</th>
              <th className="px-4 py-3 font-bold uppercase tracking-wider text-xs">Platform</th>
              <th className="px-4 py-3 font-bold uppercase tracking-wider text-xs">Version</th>
              <th className="px-4 py-3 font-bold uppercase tracking-wider text-xs">Last seen</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => {
              const Icon = PLATFORM_ICON[r.platform] || Monitor;
              const behind = isBehind(r.version);
              return (
                <tr key={r.id} className="border-b border-zinc-100" data-testid={`version-row-${r.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium">{r.name || "—"}{r.suspended && <span className="ml-2 text-xs text-zinc-400">(suspended)</span>}</div>
                    <div className="text-xs text-zinc-500">{r.email} · {r.role}</div>
                  </td>
                  <td className="px-4 py-3">{r.org_name || "—"}</td>
                  <td className="px-4 py-3">
                    {r.platform
                      ? <span className="inline-flex items-center gap-1.5"><Icon className="w-4 h-4" /> {PLATFORM_LABEL[r.platform] || r.platform}</span>
                      : <span className="text-zinc-400">—</span>}
                  </td>
                  <td className="px-4 py-3">
                    {r.version
                      ? <span className={`inline-flex items-center gap-2 font-mono text-xs ${behind ? "text-amber-700" : ""}`}>
                          {r.version}
                          {behind && <span className="px-1.5 py-0.5 rounded bg-amber-100 border border-amber-300 text-amber-800 text-[10px] font-bold uppercase tracking-wider" data-testid={`version-behind-${r.id}`}>Update pending</span>}
                        </span>
                      : <span className="text-zinc-400">Not seen yet</span>}
                  </td>
                  <td className="px-4 py-3 text-xs text-zinc-500">{fmtWhen(r.last_seen_at)}</td>
                </tr>
              );
            })}
            {!filtered.length && (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-zinc-500">No matching users.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}


export default function PlatformAdmin() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "overview";
  const setTab = (value) => setParams({ tab: value });
  return (
    <div data-testid="platform-page" className="platform-page">
      <PageHeader
        tag="/ platform admin"
        title={PLATFORM_GROUPS.flatMap(g => g[1]).find(i => i[0] === tab)?.[1] || "Platform Admin"}
        subtitle="Manage every customer organisation, billing, support and platform broadcasts from one place."
      />
      {tab === "overview" && <PlatformOverview />}
      {tab === "success" && <CustomerSuccess />}
      {tab === "usage" && <div className="pa-legacy"><CommandCentre onNavigate={setTab} usageOnly /></div>}
      {tab === "traffic" && <TrafficPanel />}
      {tab === "orgs" && <CRMRegister renderTools={org => <OrgsPanel initialOrg={org} />} />}
      {tab === "chase" && <ChasePanel />}
      {tab === "winback" && <WinBackPanel />}
      {tab === "emails" && <EmailPreviewPanel />}
      {tab === "features" && <FeatureRequestsPanel />}
      {tab === "have-your-say" && <HaveYourSayPanel />}
      {tab === "crashes" && <PlatformRecords kind="crashes" />}
      {tab === "versions" && <ClientVersionsPanel />}
      {tab === "users" && <CRMRegister people renderTools={person => <UsersPanel initialUser={person} />} />}
      {tab === "billing" && <BillingPanel />}
      {tab === "finance" && <PlatformFinance />}
      {tab === "billing-diagnostics" && <BillingDiagnostics />}
      {tab === "analytics" && <AnalyticsPanel />}
      {tab === "audit" && <PlatformRecords kind="audit" />}
      {tab === "support-sessions" && <ImpersonationAuditPanel />}
      {tab === "support" && <PlatformRecords kind="support" renderSupport={(ticket, onClose) => <TicketDialog ticket={ticket} onClose={onClose} />} />}
      {tab === "broadcast" && <BroadcastPanel />}
      {tab === "campaigns" && <CustomerCampaigns legacy={<CampaignsPanel />} />}
      {tab === "onboarding" && <OnboardingPanel />}
      {tab === "guided-onboarding" && <OnboardingControlPanel />}
      {tab === "ads" && <AdsPanel />}
      {tab === "brand" && <BrandPanel />}
      {tab === "biz-profiles" && <BusinessProfilesPanel />}
      {tab === "kiri" && <KiriControlPanel />}
      {tab === "system" && <SystemHealthPanel />}
    </div>
  );
}

/* ====== Overview ====== */
function OverviewPanel() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/platform/stats").then((r) => setStats(r.data)); }, []);
  if (!stats) return <div className="text-zinc-500">Loading…</div>;
  return (
    <div className="space-y-6" data-testid="platform-overview">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Organisations" value={stats.total_orgs} sub={`${stats.new_orgs_30d} new · 30d`} />
        <StatCard label="Total users" value={stats.total_users} sub={`${stats.new_users_30d} new · 30d`} />
        <StatCard label="MRR" value={fmtMoney(stats.mrr_nzd)} sub={`${stats.active_subs} paying`} accent />
        <StatCard label="Open tickets" value={stats.open_tickets} sub={`${stats.suspended_orgs} suspended orgs`} />
      </div>

      {/* Trial accounting strip */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4" data-testid="platform-trial-strip">
        <StatCard label="Trials · active" value={stats.trial_orgs} sub="14-day default" />
        <StatCard label="Trials · expiring soon" value={stats.trials_expiring_soon ?? 0}
                  sub="within 3 days"
                  alert={(stats.trials_expiring_soon ?? 0) > 0} />
        <StatCard label="Trials · expired" value={stats.trials_expired ?? 0}
                  sub="needs conversion or suspend"
                  alert={(stats.trials_expired ?? 0) > 0} danger />
      </div>
      <div className="hard-card p-5">
        <div className="label-tag mb-3">Plan distribution</div>
        <div className="flex flex-wrap gap-2">
          {Object.entries(stats.plan_distribution).map(([k, v]) => (
            <span key={k} className="inline-flex items-center gap-2 bg-zinc-100 border border-zinc-300 px-3 py-1.5">
              <span className="font-bold uppercase tracking-wider text-xs">{k}</span>
              <span className="font-display font-bold text-lg">{v}</span>
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, sub, accent, alert, danger }) {
  let cls = "";
  if (accent) cls = "bg-[#09090B] text-white";
  else if (danger) cls = "bg-red-50 border-2 border-red-500";
  else if (alert) cls = "bg-yellow-50 border-2 border-yellow-500";
  return (
    <div className={`hard-card p-5 ${cls}`} data-testid={`platform-stat-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
      <div className={`label-tag ${accent ? "!text-[#FF5F15]" : danger ? "!text-red-700" : alert ? "!text-yellow-800" : ""}`}>{label}</div>
      <div className={`font-display font-black text-3xl mt-1 ${danger ? "text-red-700" : alert ? "text-yellow-900" : ""}`}>{value}</div>
      {sub && <div className={`text-[10px] font-mono uppercase tracking-widest mt-1 ${accent ? "text-zinc-400" : danger ? "text-red-700" : alert ? "text-yellow-800" : "text-zinc-500"}`}>{sub}</div>}
    </div>
  );
}

/* ====== Organisations ====== */
function OrgsPanel({ initialOrg }) {
  const [orgs, setOrgs] = useState([]);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const load = () => api.get(`/platform/crm/organisations/${initialOrg.id}`).then(r => setOrgs([r.data]));
  useEffect(() => { load(); }, [q]);

  const suspend = async (o) => {
    const reason = window.prompt(`Suspend "${o.name}"? Optional reason:`, "");
    if (reason === null) return;
    await api.post(`/platform/orgs/${o.id}/suspend`, null, { params: reason ? { reason } : {} });
    toast.success(`${o.name} suspended`);
    load();
  };
  const unsuspend = async (o) => {
    await api.post(`/platform/orgs/${o.id}/unsuspend`);
    toast.success(`${o.name} reactivated`);
    load();
  };
  const approve = async (o) => {
    await api.post(`/platform/orgs/${o.id}/approve`);
    toast.success(`${o.name} approved — they can now sign in`);
    load();
  };
  const reject = async (o) => {
    const reason = window.prompt(`Reject "${o.name}"? Optional reason (shown in audit log):`, "");
    if (reason === null) return;
    await api.post(`/platform/orgs/${o.id}/reject`, { reason });
    toast.success(`${o.name} rejected`);
    load();
  };
  const del = async (o) => {
    if (!window.confirm(`DELETE "${o.name}" and ALL their data? This cannot be undone.`)) return;
    if (window.prompt(`Type the org name to confirm:`) !== o.name) {
      toast.error("Name did not match — cancelled");
      return;
    }
    await api.delete(`/platform/orgs/${o.id}`);
    toast.success("Organisation deleted");
    load();
  };

  const impersonate = async (o) => {
    if (!window.confirm(
      `Log in as the owner of "${o.name}" for support work?\n\nEvery action is audited. Your original super-admin session will be restored when you click "Return to super admin" in the banner.`
    )) return;
    try {
      const { data } = await api.post(`/platform/orgs/${o.id}/impersonate`);
      // Preserve the original super-admin Bearer token so /api requests
      // that fall back to localStorage still find their way home.
      const currentToken = localStorage.getItem("fl_token");
      if (currentToken) localStorage.setItem("fl_sa_token", currentToken);
      if (data.access_token) localStorage.setItem("fl_token", data.access_token);
      toast.success(`Logged in as ${data.target_user.name}`);
      // Hard reload so every open component re-fetches /auth/me and picks up
      // the new impersonated identity.
      window.location.replace("/app");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Impersonation failed");
    }
  };

  return (
    <div className="space-y-4" data-testid="orgs-panel">
      {orgs.some((o) => o.approval_status === "pending") && (
        <div className="hard-card border-l-4 border-l-[#FF5F15] bg-orange-50 p-4" data-testid="pending-approvals-banner">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 bg-[#FF5F15] text-white rounded-full flex items-center justify-center font-display font-black shrink-0">
              {orgs.filter((o) => o.approval_status === "pending").length}
            </div>
            <div className="flex-1">
              <div className="font-display font-bold uppercase tracking-tight">Companies awaiting approval</div>
              <div className="text-xs text-zinc-600 mt-0.5">New registrations can't sign in until you approve them. Scroll down to review each one.</div>
            </div>
          </div>
        </div>
      )}
      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search organisations…"
            className="pl-8 rounded-sm border-zinc-400" data-testid="orgs-search" />
        </div>
        <div className="text-xs font-mono text-zinc-500 uppercase tracking-widest">{orgs.length} total</div>
      </div>
      <div className="hard-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-100 text-[10px] font-bold uppercase tracking-wider text-zinc-700">
            <tr>
              <th className="text-left p-3">Name</th>
              <th className="text-left p-3">Plan</th>
              <th className="text-left p-3">Trial</th>
              <th className="text-right p-3">Users</th>
              <th className="text-left p-3">Tier check</th>
              <th className="text-right p-3">Storage</th>
              <th className="text-left p-3">Last login</th>
              <th className="text-right p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {orgs.map((o) => (
              <tr key={o.id} className="border-t border-zinc-200 hover:bg-zinc-50" data-testid={`org-row-${o.id}`}>
                <td className="p-3 font-bold">
                  <div className="flex items-center gap-2">
                    {o.name}
                    <span className="text-xs font-normal">{o.operating_profile?.version === 1 && o.operating_profile?.mode === "TRADES_SERVICES" ? "Trades & Services" : "Civil Construction"}{o.industry ? ` · ${o.industry.replaceAll("_", " ")}` : ""}</span>
                    {o.approval_status === "pending" && (
                      <span className="inline-flex items-center gap-1 bg-[#FF5F15] text-white text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5" data-testid={`org-pending-${o.id}`}>
                        Pending
                      </span>
                    )}
                    {o.approval_status === "pending" && o.signup_verification?.reason && (
                      <span className="text-xs font-normal text-amber-700" data-testid={`org-pending-reason-${o.id}`}>{o.signup_verification.reason}. Check the NZ Companies Register before approving.</span>
                    )}
                    {o.signup_verification?.state === "verified" && (
                      <span className="text-xs font-normal text-emerald-700" data-testid={`org-verified-${o.id}`}>NZBN {o.nzbn} verified</span>
                    )}
                    {o.approval_status === "rejected" && (
                      <span className="inline-flex items-center gap-1 bg-zinc-900 text-white text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5">
                        Rejected
                      </span>
                    )}
                  </div>
                </td>
                <td className="p-3">
                  <PlanBadge plan={o.plan} status={o.plan_status} />
                </td>
                <td className="p-3" data-testid={`org-trial-${o.id}`}>
                  <TrialBadge org={o} />
                </td>
                <td className="p-3 text-right font-mono">{o.active_users_count ?? o.users_count}/{o.user_limit ?? o.seats_limit ?? "∞"}</td>
                <td className="p-3">
                  <TierSuggestion org={o} onSetTier={async (tierId) => {
                    if (!window.confirm(`Move ${o.name} to the ${tierId.toUpperCase()} tier?`)) return;
                    await api.post(`/platform/orgs/${o.id}/set_tier`, { tier: tierId });
                    toast.success(`${o.name} moved to ${tierId}`);
                    load();
                  }} />
                </td>
                <td className="p-3 text-right font-mono">{bytes(o.storage_bytes)}</td>
                <td className="p-3 text-xs font-mono">{fmtDate(o.last_login_at)}</td>
                <td className="p-3 text-right whitespace-nowrap">
                  {o.approval_status === "pending" && (
                    <>
                      <Button size="sm" onClick={() => approve(o)}
                        className="rounded-sm text-xs font-bold uppercase bg-[#FF5F15] hover:bg-[#E65100] text-white mr-1"
                        data-testid={`org-approve-${o.id}`}>
                        Approve
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => reject(o)}
                        className="rounded-sm text-xs font-bold uppercase border-zinc-400 text-zinc-700 mr-1"
                        data-testid={`org-reject-${o.id}`}>
                        Reject
                      </Button>
                    </>
                  )}
                  <Button size="sm" variant="outline" onClick={() => setEditing(o)}
                    className="rounded-sm text-xs font-bold uppercase mr-1" data-testid={`org-edit-${o.id}`}>Edit</Button>
                  {o.plan_status === "suspended" ? (
                    <Button size="sm" variant="outline" onClick={() => unsuspend(o)}
                      className="rounded-sm text-xs font-bold uppercase border-green-500 text-green-700 mr-1"
                      data-testid={`org-unsuspend-${o.id}`}>
                      <Power className="w-3 h-3 mr-1" /> Reactivate
                    </Button>
                  ) : (
                    <Button size="sm" variant="outline" onClick={() => suspend(o)}
                      className="rounded-sm text-xs font-bold uppercase border-yellow-500 text-yellow-700 mr-1"
                      data-testid={`org-suspend-${o.id}`}>
                      <PowerOff className="w-3 h-3 mr-1" /> Suspend
                    </Button>
                  )}
                  {o.plan_status !== "suspended" && o.approval_status !== "pending" && (
                    <Button size="sm" variant="outline" onClick={() => impersonate(o)}
                      className="rounded-sm text-xs font-bold uppercase border-[#FF5F15] text-[#FF5F15] hover:bg-orange-50 mr-1"
                      data-testid={`org-impersonate-${o.id}`}
                      title="Log in as this org's owner for support work">
                      <LogIn className="w-3 h-3 mr-1" /> Log in as
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => del(o)}
                    className="rounded-sm text-xs font-bold uppercase text-red-600 hover:text-red-700"
                    data-testid={`org-delete-${o.id}`}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <OrgEditDialog org={editing} onClose={() => { setEditing(null); load(); }} />
    </div>
  );
}

function TierSuggestion({ org, onSetTier }) {
  const tier = org.suggested_tier;
  if (!tier) return <span className="text-xs text-zinc-400">—</span>;
  const mismatch = !!org.tier_mismatch;
  return (
    <div className="flex items-center gap-1.5">
      <span
        className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm ${
          mismatch ? "bg-yellow-100 text-yellow-900 border border-yellow-400" : "bg-zinc-100 text-zinc-600 border border-zinc-200"
        }`}
        title={`Suggested by headcount`}
        data-testid={`org-suggested-tier-${org.id}`}
      >
        {mismatch ? <FlagTriangleRight className="w-3 h-3" /> : null}
        {tier.toUpperCase()}
      </span>
      {mismatch && (
        <button
          onClick={() => onSetTier(tier)}
          className="text-[10px] font-bold uppercase tracking-wider text-[#FF5F15] hover:text-[#E65100] underline-offset-2 hover:underline"
          data-testid={`org-apply-tier-${org.id}`}
        >
          Upgrade
        </button>
      )}
    </div>
  );
}

function PlanBadge({ plan, status }) {
  const planColors = {
    trial: "bg-blue-100 text-blue-800 border-blue-300",
    crew: "bg-zinc-100 text-zinc-800 border-zinc-300",
    site: "bg-[#FF5F15]/20 text-[#9A3500] border-[#FF5F15]",
    enterprise: "bg-zinc-900 text-white border-zinc-900",
    // legacy
    starter: "bg-zinc-100 text-zinc-800 border-zinc-300",
    pro: "bg-[#FF5F15] text-white border-[#E65100]",
  };
  const statusColors = {
    active: "bg-green-100 text-green-800",
    trial: "bg-blue-100 text-blue-800",
    past_due: "bg-yellow-100 text-yellow-800",
    suspended: "bg-red-600 text-white",
    cancelled: "bg-zinc-200 text-zinc-600",
  };
  return (
    <div className="flex flex-wrap gap-1">
      <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 border rounded-sm ${planColors[plan || "trial"] || planColors.trial}`}>{plan || "trial"}</span>
      <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm ${statusColors[status || "trial"] || statusColors.trial}`}>{status || "trial"}</span>
    </div>
  );
}

function OrgEditDialog({ org, onClose }) {
  const [form, setForm] = useState(null);
  const [flagsText, setFlagsText] = useState("");
  useEffect(() => {
    if (!org) return setForm(null);
    setForm({
      name: org.name || "",
      plan: org.plan || "trial",
      plan_status: org.plan_status || "trial",
      trial_ends_at: org.trial_ends_at || "",
      seats_limit: org.seats_limit || "",
      monthly_price: org.monthly_price || "",
      billing_email: org.billing_email || "",
      notes: org.notes || "",
    });
    setFlagsText(JSON.stringify(org.feature_flags || {}, null, 2));
  }, [org]);

  if (!org || !form) return null;

  const save = async () => {
    let feature_flags = null;
    if (flagsText.trim()) {
      try { feature_flags = JSON.parse(flagsText); }
      catch { toast.error("Feature flags must be valid JSON"); return; }
    }
    const payload = {
      ...form,
      seats_limit: form.seats_limit ? Number(form.seats_limit) : null,
      monthly_price: form.monthly_price ? Number(form.monthly_price) : null,
      trial_ends_at: form.trial_ends_at || null,
      feature_flags,
    };
    Object.keys(payload).forEach((k) => payload[k] === "" && delete payload[k]);
    try {
      await api.patch(`/platform/orgs/${org.id}`, payload);
      toast.success("Organisation updated");
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: typeof v === "object" ? v.target.value : v }));

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="rounded-sm max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display uppercase">Edit {org.name}</DialogTitle>
          <DialogDescription>Update billing plan, seats, trial expiry and feature flags.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Field label="Name">
            <Input value={form.name} onChange={set("name")} className="rounded-sm border-zinc-400" data-testid="org-edit-name" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Plan">
              <Select value={form.plan} onValueChange={set("plan")}>
                <SelectTrigger className="rounded-sm border-zinc-400" data-testid="org-edit-plan"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["trial", "crew", "site", "yard", "white_label", "fleet", "enterprise", "starter", "pro", "custom"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Status">
              <Select value={form.plan_status} onValueChange={set("plan_status")}>
                <SelectTrigger className="rounded-sm border-zinc-400" data-testid="org-edit-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["trial", "active", "past_due", "suspended", "cancelled"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Trial expires">
              <Input type="date" value={form.trial_ends_at?.slice(0, 10) || ""} onChange={set("trial_ends_at")} className="rounded-sm border-zinc-400" data-testid="org-edit-trial-ends" />
            </Field>
            <Field label="Seats limit">
              <Input type="number" value={form.seats_limit} onChange={set("seats_limit")} className="rounded-sm border-zinc-400" placeholder="∞" data-testid="org-edit-seats" />
            </Field>
            <Field label="Monthly price (NZ$)">
              <Input type="number" step="0.01" value={form.monthly_price} onChange={set("monthly_price")} className="rounded-sm border-zinc-400" data-testid="org-edit-price" />
            </Field>
            <Field label="Billing email">
              <Input type="email" value={form.billing_email} onChange={set("billing_email")} className="rounded-sm border-zinc-400" data-testid="org-edit-billing-email" />
            </Field>
          </div>
          <Field label="Feature flags (JSON)">
            <Textarea rows={4} value={flagsText} onChange={(e) => setFlagsText(e.target.value)} className="rounded-sm border-zinc-400 font-mono text-xs" placeholder='{"chat_beta": true}' data-testid="org-edit-flags" />
          </Field>
          <Field label="Internal notes">
            <Textarea rows={2} value={form.notes} onChange={set("notes")} className="rounded-sm border-zinc-400" data-testid="org-edit-notes" />
          </Field>
        </div>
        <DialogFooter>
          <Button onClick={save} className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] uppercase tracking-wider font-bold" data-testid="org-edit-save">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="label-tag">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

/* ====== Users ====== */
function UsersPanel({ initialUser }) {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [tempPwd, setTempPwd] = useState(null);
  const [history, setHistory] = useState(null);
  const [movingUser, setMovingUser] = useState(null);
  const load = () => api.get(`/platform/crm/people/${initialUser.id}`).then(r => setUsers([r.data]));
  useEffect(() => { load(); }, [q]);

  const reset = async (u) => {
    if (!window.confirm(`Reset password for ${u.email}? They'll need to log in with the new temp password.`)) return;
    const { data } = await api.post(`/platform/users/${u.id}/reset_password`);
    setTempPwd({ user: u, password: data.temp_password });
  };
  const suspend = async (u) => {
    await api.post(`/platform/users/${u.id}/suspend`);
    toast.success(`${u.email} suspended`);
    load();
  };
  const unsuspend = async (u) => {
    try {
      await api.post(`/platform/users/${u.id}/unsuspend`);
      toast.success(`${u.email} reactivated`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Reactivation failed"); }
  };
  const viewHistory = async (u) => {
    const { data } = await api.get(`/platform/users/${u.id}/login_history`);
    setHistory({ user: u, rows: data });
  };

  return (
    <div className="space-y-4" data-testid="users-panel">
      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or email…"
            className="pl-8 rounded-sm border-zinc-400" data-testid="users-search" />
        </div>
        <div className="text-xs font-mono text-zinc-500 uppercase tracking-widest">{users.length}</div>
      </div>
      <div className="hard-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-100 text-[10px] font-bold uppercase tracking-wider text-zinc-700">
            <tr>
              <th className="text-left p-3">Email / name</th>
              <th className="text-left p-3">Org</th>
              <th className="text-left p-3">Role</th>
              <th className="text-left p-3">Last login</th>
              <th className="text-left p-3">Status</th>
              <th className="text-right p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-t border-zinc-200" data-testid={`user-row-${u.id}`}>
                <td className="p-3">
                  <div className="font-bold">{u.name}</div>
                  <div className="text-xs font-mono text-zinc-500">{u.email}</div>
                </td>
                <td className="p-3 text-xs">{u.org_name || <span className="text-zinc-400 italic">platform</span>}</td>
                <td className="p-3 text-xs font-bold uppercase tracking-wider">{u.role}</td>
                <td className="p-3 text-xs font-mono">{fmtDate(u.last_login_at)}</td>
                <td className="p-3">
                  {u.suspended_at
                    ? <span className="text-[10px] font-bold uppercase tracking-wider bg-red-600 text-white px-1.5 py-0.5 rounded-sm">Suspended</span>
                    : <span className="text-[10px] font-bold uppercase tracking-wider bg-green-100 text-green-800 px-1.5 py-0.5 rounded-sm">Active</span>}
                </td>
                <td className="p-3 text-right whitespace-nowrap">
                  <Button size="sm" variant="outline" onClick={() => reset(u)}
                    className="rounded-sm text-xs font-bold uppercase mr-1" data-testid={`user-reset-${u.id}`}>
                    <KeyRound className="w-3 h-3 mr-1" /> Reset pw
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => viewHistory(u)}
                    className="rounded-sm text-xs font-bold uppercase mr-1" data-testid={`user-history-${u.id}`}>
                    <Activity className="w-3 h-3 mr-1" /> History
                  </Button>
                  {u.role !== "super_admin" && (
                    <Button size="sm" variant="outline" onClick={() => setMovingUser(u)}
                      className="rounded-sm text-xs font-bold uppercase mr-1" data-testid={`user-move-org-${u.id}`}>
                      <Building2 className="w-3 h-3 mr-1" /> Move org
                    </Button>
                  )}
                  {u.suspended_at ? (
                    <Button size="sm" variant="outline" onClick={() => unsuspend(u)} className="rounded-sm text-xs font-bold uppercase border-green-500 text-green-700" data-testid={`user-unsuspend-${u.id}`}>
                      <Power className="w-3 h-3 mr-1" /> Activate
                    </Button>
                  ) : (
                    <Button size="sm" variant="outline" onClick={() => suspend(u)} className="rounded-sm text-xs font-bold uppercase border-yellow-500 text-yellow-700" data-testid={`user-suspend-${u.id}`}>
                      <PowerOff className="w-3 h-3 mr-1" /> Suspend
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <TempPasswordDialog data={tempPwd} onClose={() => setTempPwd(null)} />
      <LoginHistoryDialog data={history} onClose={() => setHistory(null)} />
      <MoveOrgDialog user={movingUser} onClose={() => setMovingUser(null)} onDone={() => { setMovingUser(null); load(); }} />
    </div>
  );
}

function MoveOrgDialog({ user, onClose, onDone }) {
  const [orgs, setOrgs] = useState([]);
  const [orgSearch, setOrgSearch] = useState("");
  useEffect(() => { if (!user) return; const c = new AbortController(); const t = setTimeout(() => api.get("/platform/crm/organisations", { params: { q: orgSearch }, signal: c.signal }).then(r => setOrgs(r.data.items)).catch(() => {}), 250); return () => { clearTimeout(t); c.abort(); }; }, [user, orgSearch]);
  const [orgId, setOrgId] = useState("");
  const [role, setRole] = useState("");
  const [carryCerts, setCarryCerts] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user) {
      setOrgId("");
      setRole(user.role || "field");
      setCarryCerts(true);

    }
  }, [user]);

  if (!user) return null;

  const move = async () => {
    if (!orgId) { toast.error("Pick the new organisation"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/platform/users/${user.id}/move_org`,
        { org_id: orgId, role: role || null, carry_competencies: carryCerts });
      toast.success(`${user.email} moved to ${data.org_name}${data.competencies_carried ? ` · ${data.competencies_carried} competenc${data.competencies_carried === 1 ? "y" : "ies"} carried over` : ""}`);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Move failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="rounded-sm max-w-md" data-testid="move-org-dialog">
        <DialogHeader>
          <DialogTitle className="font-display uppercase">Move to another org</DialogTitle>
          <DialogDescription>
            <b>{user.name}</b> ({user.email}) is currently in <b>{user.org_name || "no org"}</b>.
            Their timesheets and paperwork stay with the old company — only the account moves.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-zinc-600 block mb-1.5">New organisation</label>
            <Input aria-label="Search organisations" placeholder="Search organisations…" value={orgSearch} onChange={e => setOrgSearch(e.target.value)} />
              <select value={orgId} onChange={(e) => setOrgId(e.target.value)}
              className="w-full h-10 px-3 text-sm bg-white border border-zinc-300 rounded-sm focus:outline-none focus:border-[#FF5F15]"
              data-testid="move-org-select">
              <option value="">Select organisation…</option>
              {orgs.filter((o) => o.id !== user.org_id).map((o) => (
                <option key={o.id} value={o.id}>{o.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-zinc-600 block mb-1.5">Role in the new org</label>
            <select value={role} onChange={(e) => setRole(e.target.value)}
              className="w-full h-10 px-3 text-sm bg-white border border-zinc-300 rounded-sm focus:outline-none focus:border-[#FF5F15]"
              data-testid="move-org-role-select">
              <option value="field">Field worker</option>
              <option value="office">Office</option>
              <option value="admin">Admin</option>
              <option value="quantity_surveyor">Quantity surveyor</option>
              <option value="mechanic">Mechanic</option>
            </select>
          </div>
          <label className="flex items-start gap-2 cursor-pointer bg-zinc-50 border border-zinc-300 p-3">
            <input type="checkbox" checked={carryCerts} onChange={(e) => setCarryCerts(e.target.checked)}
              className="mt-0.5 accent-[#FF5F15]" data-testid="move-org-carry-certs" />
            <span className="text-xs text-zinc-700">
              <b>Carry over competencies & certs</b><br />
              Copies their tickets, licences and cert documents to the new company
              (matching or creating competency types by name). The old company keeps its records.
            </span>
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-sm uppercase tracking-wider font-bold text-xs" data-testid="move-org-cancel">Cancel</Button>
          <Button onClick={move} disabled={busy || !orgId}
            className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase tracking-wider font-bold text-xs"
            data-testid="move-org-confirm">
            {busy ? "Moving…" : "Move user"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TempPasswordDialog({ data, onClose }) {
  if (!data) return null;
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="rounded-sm max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display uppercase">Temporary password</DialogTitle>
          <DialogDescription>Share this with {data.user.email} privately. They should change it after login.</DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-2 p-3 bg-zinc-100 border border-zinc-300 font-mono text-lg">
          <span className="flex-1 break-all" data-testid="temp-password-display">{data.password}</span>
          <Button size="sm" onClick={() => { navigator.clipboard.writeText(data.password); toast.success("Copied"); }} className="rounded-sm"><Copy className="w-3.5 h-3.5" /></Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function LoginHistoryDialog({ data, onClose }) {
  if (!data) return null;
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="rounded-sm max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-display uppercase">{data.user.email} login history</DialogTitle>
        </DialogHeader>
        <div className="max-h-96 overflow-y-auto" data-testid="login-history-table">
          <table className="w-full text-xs">
            <thead className="bg-zinc-100 text-[10px] font-bold uppercase tracking-wider sticky top-0">
              <tr>
                <th className="text-left p-2">When</th>
                <th className="text-left p-2">IP</th>
                <th className="text-left p-2">Status</th>
                <th className="text-left p-2">User-agent</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.length === 0 && <tr><td colSpan="4" className="p-4 text-zinc-400 italic">No login attempts recorded.</td></tr>}
              {data.rows.map((r, i) => (
                <tr key={i} className="border-t border-zinc-200">
                  <td className="p-2 font-mono">{fmtDate(r.created_at)}</td>
                  <td className="p-2 font-mono">{r.ip}</td>
                  <td className="p-2">{r.success
                    ? <span className="text-green-700 font-bold uppercase text-[10px]">OK</span>
                    : <span className="text-red-700 font-bold uppercase text-[10px]">FAIL</span>}</td>
                  <td className="p-2 truncate max-w-xs">{r.user_agent}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ====== Billing ====== */
function BillingPanel() {
  const [, setBillingParams] = useSearchParams();
  const [subPage, setSubPage] = useState(1);
  const [invoicePage, setInvoicePage] = useState(1);
  const [subPaging, setSubPaging] = useState(null);
  const [invoicePaging, setInvoicePaging] = useState(null);
  const [summary, setSummary] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [subs, setSubs] = useState([]);
  const [creating, setCreating] = useState(false);
  const load = async () => {
    const [s, inv, o, sb] = await Promise.all([
      api.get("/platform/billing/summary"),
      api.get("/platform/billing/invoices", { params: { page: invoicePage } }),
      api.get("/platform/crm/organisations"),
      api.get("/platform/subscriptions", { params: { page: subPage } }),
    ]);
    // These billing endpoints return arrays; also accept paginated responses.
    const invoiceRows = Array.isArray(inv.data) ? inv.data : inv.data?.items;
    const subscriptionRows = Array.isArray(sb.data) ? sb.data : sb.data?.items;
    setSummary(s.data);
    setInvoices(Array.isArray(invoiceRows) ? invoiceRows : []);
    setInvoicePaging(!Array.isArray(inv.data) && Array.isArray(inv.data?.items) ? inv.data : null);
    setOrgs(Array.isArray(o.data?.items) ? o.data.items : []);
    setSubs(Array.isArray(subscriptionRows) ? subscriptionRows : []);
    setSubPaging(!Array.isArray(sb.data) && Array.isArray(sb.data?.items) ? sb.data : null);
  };
  useEffect(() => { load().catch(() => toast.error("Could not load billing")); }, [subPage, invoicePage]);

  const markPaid = async (inv) => {
    await api.patch(`/platform/billing/invoices/${inv.id}`, { status: "paid" });
    toast.success("Marked paid");
    load();
  };

  if (!summary) return <div className="text-zinc-500">Loading…</div>;
  return (
    <div className="space-y-4" data-testid="billing-panel">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="MRR active" value={fmtMoney(summary.mrr_active)} accent />
        <StatCard label="Trial potential" value={fmtMoney(summary.mrr_trial_potential)} />
        <StatCard label="Churn rate · 30d" value={`${summary.churn_rate_30d}%`} sub={`${summary.cancelled_30d} cancelled`} />
        <StatCard label="Outstanding" value={fmtMoney(summary.outstanding_invoices_amount)} sub={`${summary.outstanding_invoices_count} invoices`} />
      </div>
      <div className="flex items-center justify-between">
        <div className="label-tag">Subscriptions — soonest ending first</div>
        <button
          onClick={async () => {
            try {
              toast.info("Resyncing subscriptions from Stripe…");
              const { data } = await api.post("/platform/subscriptions/resync");
              toast.success(`Resynced ${data.synced.length}/${data.total} Stripe subscriptions`);
              load();
            } catch (err) {
              toast.error(err.response?.data?.detail || "Resync failed");
            }
          }}
          className="text-[10px] font-bold uppercase tracking-widest border-2 border-zinc-900 px-3 py-1.5 hover:bg-zinc-900 hover:text-white transition-colors"
          data-testid="platform-resync-stripe-btn"
        >
          Resync from Stripe
        </button>
      </div>
      <div className="hard-card overflow-x-auto" data-testid="platform-subscriptions-table">
        <table className="w-full text-sm">
          <thead className="bg-zinc-100 text-[10px] font-bold uppercase tracking-wider text-zinc-700">
            <tr>
              <th className="text-left p-3">Org</th>
              <th className="text-left p-3">Plan</th>
              <th className="text-left p-3">Status</th>
              <th className="text-left p-3">$/mo</th>
              <th className="text-left p-3">Ends / renews</th>
              <th className="text-left p-3">Days left</th>
              <th className="text-left p-3">Auto-renew</th>
            </tr>
          </thead>
          <tbody>
            {subs.map((s) => {
              const d = s.days_left;
              const urgency = d == null ? "" : d < 0 ? "text-red-700 font-bold" : d <= 7 ? "text-red-600 font-bold" : d <= 14 ? "text-amber-600 font-bold" : "";
              return (
                <tr key={s.org_id} className={`border-b border-zinc-100 ${d != null && d <= 7 ? "bg-red-50/60" : ""}`} data-testid={`platform-sub-${s.org_id}`}>
                  <td className="p-3 font-medium">{s.name}<div className="text-[10px] text-zinc-500">{s.billing_management === "stripe" ? "Stripe-managed" : "Manual / legacy"}</div></td>
                  <td className="p-3 uppercase text-xs font-bold">{s.plan}<div className="font-normal normal-case">{s.active_users} / {s.user_limit ?? "custom"} active users</div>{s.pending_downgrade && <div className="font-normal normal-case">{s.pending_downgrade.target_plan} from {fmtDay(s.pending_downgrade.effective_at)}</div>}</td>
                  <td className="p-3 text-xs">{s.stripe_status || s.plan_status}{s.payment_issue && <div className="text-amber-700">Payment needs attention</div>}</td>
                  <td className="p-3 text-xs font-mono">{s.monthly_price ? fmtMoney(s.monthly_price) : "—"}</td>
                  <td className="p-3 text-xs">
                    <input
                      type="date"
                      value={s.ends_at ? String(s.ends_at).slice(0, 10) : ""}
                      disabled={s.billing_management === "stripe"}
                      title={s.billing_management === "stripe" ? "Renewal date is managed by Stripe" : "Edit manual renewal date"}
                      onChange={async (e) => {
                        const v = e.target.value;
                        if (!v) return;
                        try {
                          await api.patch(`/platform/subscriptions/${s.org_id}`, { ends_at: v });
                          toast.success(`${s.name}: ${s.kind === "trial" ? "trial end" : "renewal"} set to ${new Date(v).toLocaleDateString("en-NZ")}`);
                          load();
                        } catch (err) {
                          toast.error(err.response?.data?.detail || "Couldn't set date");
                        }
                      }}
                      className="h-7 rounded-sm border border-zinc-300 bg-white px-1.5 text-xs font-mono"
                      data-testid={`platform-sub-end-${s.org_id}`}
                    />
                  </td>
                  <td className={`p-3 text-xs font-mono ${urgency}`}>{d == null ? "—" : d < 0 ? `${Math.abs(d)}d overdue` : `${d}d`}</td>
                  <td className="p-3 text-xs">
                    {s.auto_renew == null ? "—" : s.auto_renew
                      ? <span className="text-emerald-700 font-bold">ON</span>
                      : <span className="text-red-600 font-bold">OFF</span>}
                  </td>
                </tr>
              );
            })}
            {subs.length === 0 && <tr><td className="p-3 text-zinc-400 text-xs" colSpan={7}>No organisations.</td></tr>}
          </tbody>
        </table>
      </div>
      {subPaging && <Pager data={subPaging} onPage={setSubPage} />}
      <div className="flex items-center justify-between">
        <div className="label-tag">Legacy invoice records · new invoices and GST are in Finance</div>
        <Button onClick={() => setBillingParams({ tab: "finance" })} className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] uppercase tracking-wider font-bold" data-testid="invoice-new">
          <DollarSign className="w-4 h-4 mr-1" /> New invoice
        </Button>
      </div>
      <div className="hard-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-100 text-[10px] font-bold uppercase tracking-wider text-zinc-700">
            <tr>
              <th className="text-left p-3">Org</th>
              <th className="text-left p-3">Period</th>
              <th className="text-right p-3">Amount</th>
              <th className="text-left p-3">Status</th>
              <th className="text-left p-3">Created</th>
              <th className="text-right p-3"></th>
            </tr>
          </thead>
          <tbody>
            {invoices.length === 0 && <tr><td colSpan="6" className="p-6 text-center text-zinc-400 italic">No invoices yet.</td></tr>}
            {invoices.map((i) => (
              <tr key={i.id} className="border-t border-zinc-200" data-testid={`invoice-${i.id}`}>
                <td className="p-3 font-bold">{i.org_name}</td>
                <td className="p-3 text-xs font-mono">{fmtDay(i.period_start)} → {fmtDay(i.period_end)}</td>
                <td className="p-3 text-right font-mono">{fmtMoney(i.amount)}</td>
                <td className="p-3"><InvoiceStatus s={i.status} /></td>
                <td className="p-3 text-xs font-mono">{fmtDate(i.created_at)}</td>
                <td className="p-3 text-right">
                  {i.status !== "paid" && (
                    <Button size="sm" variant="outline" onClick={() => markPaid(i)}
                      className="rounded-sm text-xs font-bold uppercase border-green-500 text-green-700" data-testid={`invoice-paid-${i.id}`}>
                      Mark paid
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {invoicePaging && <Pager data={invoicePaging} onPage={setInvoicePage} />}
      <InvoiceCreateDialog open={creating} orgs={orgs} onClose={() => setCreating(false)} onCreated={() => { setCreating(false); load(); }} />
    </div>
  );
}

function InvoiceStatus({ s }) {
  const map = {
    draft: "bg-zinc-200 text-zinc-700",
    sent: "bg-blue-100 text-blue-800",
    paid: "bg-green-100 text-green-800",
    overdue: "bg-red-100 text-red-800",
    void: "bg-zinc-100 text-zinc-500 line-through",
  };
  return <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm ${map[s] || map.draft}`}>{s}</span>;
}

function InvoiceCreateDialog({ open, orgs: initialOrgs, onClose, onCreated }) {
  const [orgs, setOrgs] = useState(initialOrgs || []);
  const [orgSearch, setOrgSearch] = useState("");
  useEffect(() => { if (!open) return; const c = new AbortController(); const t = setTimeout(() => api.get("/platform/crm/organisations", { params: { q: orgSearch }, signal: c.signal }).then(r => setOrgs(r.data.items)).catch(() => {}), 250); return () => { clearTimeout(t); c.abort(); }; }, [open, orgSearch]);
  const [form, setForm] = useState({ org_id: "", amount: "", period_start: "", period_end: "", seats: "", notes: "" });
  const submit = async () => {
    if (!form.org_id || !form.amount || !form.period_start || !form.period_end) {
      toast.error("Org, amount and period are required"); return;
    }
    await api.post("/platform/billing/invoices", {
      ...form, amount: Number(form.amount),
      seats: form.seats ? Number(form.seats) : null,
    });
    toast.success("Invoice created");
    setForm({ org_id: "", amount: "", period_start: "", period_end: "", seats: "", notes: "" });
    onCreated();
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="rounded-sm max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display uppercase">New invoice</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <Input aria-label="Search organisations" placeholder="Search organisations…" value={orgSearch} onChange={e => setOrgSearch(e.target.value)} />
          <Field label="Organisation">
            <Select value={form.org_id} onValueChange={(v) => setForm((f) => ({ ...f, org_id: v }))}>
              <SelectTrigger className="rounded-sm border-zinc-400" data-testid="invoice-org-select"><SelectValue placeholder="Select…" /></SelectTrigger>
              <SelectContent>
                {orgs.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Period start"><Input type="date" value={form.period_start} onChange={(e) => setForm((f) => ({ ...f, period_start: e.target.value }))} className="rounded-sm border-zinc-400" data-testid="invoice-start" /></Field>
            <Field label="Period end"><Input type="date" value={form.period_end} onChange={(e) => setForm((f) => ({ ...f, period_end: e.target.value }))} className="rounded-sm border-zinc-400" data-testid="invoice-end" /></Field>
            <Field label="Amount NZ$"><Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} className="rounded-sm border-zinc-400" data-testid="invoice-amount" /></Field>
            <Field label="Seats"><Input type="number" value={form.seats} onChange={(e) => setForm((f) => ({ ...f, seats: e.target.value }))} className="rounded-sm border-zinc-400" data-testid="invoice-seats" /></Field>
          </div>
          <Field label="Notes"><Textarea rows={2} value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} className="rounded-sm border-zinc-400" data-testid="invoice-notes" /></Field>
        </div>
        <DialogFooter>
          <Button onClick={submit} className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] uppercase tracking-wider font-bold" data-testid="invoice-save">Create</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ====== Analytics ====== */
function AnalyticsPanel() {
  const [period, setPeriod] = useState(7);
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/platform/analytics", { params: { period_days: period } }).then((r) => setData(r.data));
  }, [period]);
  const max = useMemo(() => Math.max(1, ...(data?.dau_series?.map((d) => d.count) || [1])), [data]);
  if (!data) return <div className="text-zinc-500">Loading…</div>;
  return (
    <div className="space-y-4" data-testid="analytics-panel">
      <div className="flex items-center gap-2">
        {[7, 14, 30, 90].map((d) => (
          <Button key={d} size="sm" variant={period === d ? "default" : "outline"}
            onClick={() => setPeriod(d)}
            className={`rounded-sm uppercase tracking-wider font-bold text-xs ${period === d ? "bg-zinc-900 text-white" : ""}`}
            data-testid={`analytics-period-${d}`}>
            {d}d
          </Button>
        ))}
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard label="Jobs created" value={data.jobs_created} />
        <StatCard label="Timesheets" value={data.timesheets_created} />
        <StatCard label="Safety forms" value={data.safety_created} />
        <StatCard label="Toolbox talks" value={data.toolbox_created} />
        <StatCard label="Chat messages" value={data.chats_created} />
      </div>
      <div className="hard-card p-5">
        <div className="label-tag mb-3">Daily active users · last {period} days</div>
        {data.dau_series.length === 0 ? (
          <div className="text-zinc-400 text-xs italic">No login activity yet.</div>
        ) : (
          <div className="flex items-end gap-1 h-32" data-testid="dau-chart">
            {data.dau_series.map((d) => (
              <div key={d.date} className="flex-1 flex flex-col items-center gap-1">
                <div className="text-[10px] font-mono">{d.count}</div>
                <div className="w-full bg-[#FF5F15]" style={{ height: `${(d.count / max) * 100}%` }} />
                <div className="text-[9px] font-mono text-zinc-500 rotate-45 origin-left translate-y-2">{d.date.slice(5)}</div>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="hard-card p-5">
        <div className="label-tag mb-3">Storage usage by org (top 20)</div>
        <table className="w-full text-sm">
          <thead className="text-[10px] font-bold uppercase tracking-wider text-zinc-700">
            <tr><th className="text-left p-2">Org</th><th className="text-right p-2">Files</th><th className="text-right p-2">Size</th></tr>
          </thead>
          <tbody>
            {data.storage_by_org.map((s, i) => (
              <tr key={i} className="border-t border-zinc-200"><td className="p-2">{s.org_name}</td><td className="p-2 text-right font-mono">{s.count}</td><td className="p-2 text-right font-mono">{bytes(s.size)}</td></tr>
            ))}
          </tbody>
        </table>
      </div>
      <CivilHubAnalytics period={period} />
    </div>
  );
}

/* ====== Civil Hub analytics — anonymous public tool usage ====== */
function CivilHubAnalytics({ period }) {
  const [hub, setHub] = useState(null);
  useEffect(() => {
    api.get("/platform/civilhub/analytics", { params: { days: period } }).then((r) => setHub(r.data)).catch(() => setHub(null));
  }, [period]);
  if (!hub) return null;
  const totalEvents = Object.values(hub.totals || {}).reduce((a, b) => a + b, 0);
  const TopList = ({ title, rows, empty, testId, highlight }) => (
    <div className="hard-card p-5" data-testid={testId}>
      <div className="label-tag mb-3">{title}</div>
      {(!rows || rows.length === 0) ? (
        <div className="text-zinc-400 text-xs italic">{empty}</div>
      ) : (
        <div className="space-y-1.5">
          {rows.map((r, i) => (
            <div key={r.name} className="flex items-center justify-between text-sm border-b border-zinc-100 pb-1.5">
              <span className="flex items-center gap-2 min-w-0">
                <span className="text-[10px] font-mono text-zinc-400 w-5 shrink-0">{i + 1}.</span>
                <span className={`truncate font-mono text-xs ${highlight && i === 0 ? "font-bold text-[#FF5F15]" : "text-zinc-700"}`}>{r.name}</span>
              </span>
              <span className="font-mono text-xs font-bold shrink-0 ml-2">{r.count}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
  return (
    <div className="space-y-4" data-testid="civilhub-analytics">
      <div className="flex items-center justify-between">
        <div className="label-tag">Civil Hub · free tools · last {hub.days} days</div>
        <div className="text-xs font-mono text-zinc-500 uppercase tracking-widest">{totalEvents} events</div>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard label="Calculator runs" value={hub.totals?.calculator_started || 0} />
        <StatCard label="Hub searches" value={hub.totals?.civil_hub_search || 0} />
        <StatCard label="Searches with no result" value={hub.totals?.civil_hub_search_no_results || 0} />
        <StatCard label="Templates used" value={(hub.totals?.template_started || 0) + (hub.totals?.template_downloaded || 0)} />
        <StatCard label="CTA clicks" value={hub.totals?.fieldlink_cta_clicked || 0} />
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        <TopList title="Top tools" rows={hub.top_tools} empty="No calculator activity yet." testId="civilhub-top-tools" highlight />
        <TopList title="Top searches" rows={hub.top_searches} empty="No searches logged yet." testId="civilhub-top-searches" />
        <TopList title="Missing tools (searches with no result)" rows={hub.missing_tools} empty="No unmatched searches — nothing missing." testId="civilhub-missing-tools" highlight />
        <TopList title="Top templates" rows={hub.top_templates} empty="No template activity yet." testId="civilhub-top-templates" />
        <TopList title="CTA clicks by source" rows={hub.cta_clicks} empty="No CTA clicks yet." testId="civilhub-cta-clicks" />
      </div>
    </div>
  );
}

/* ====== Audit log ====== */
function AuditPanel() {
  const [rows, setRows] = useState([]);
  const [actionFilter, setActionFilter] = useState("");
  useEffect(() => {
    api.get("/platform/audit", { params: actionFilter ? { action: actionFilter } : {} }).then((r) => setRows(r.data));
  }, [actionFilter]);
  return (
    <div className="space-y-3" data-testid="audit-panel">
      <div className="flex items-center gap-2">
        <Input placeholder="Filter by action (e.g. org.suspend)" value={actionFilter}
          onChange={(e) => setActionFilter(e.target.value)}
          className="rounded-sm border-zinc-400 max-w-md" data-testid="audit-action-filter" />
        <div className="text-xs font-mono text-zinc-500 uppercase tracking-widest">{rows.length} events</div>
      </div>
      <div className="hard-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-100 text-[10px] font-bold uppercase tracking-wider text-zinc-700">
            <tr>
              <th className="text-left p-3">When</th>
              <th className="text-left p-3">Actor</th>
              <th className="text-left p-3">Action</th>
              <th className="text-left p-3">Target</th>
              <th className="text-left p-3">Note</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan="5" className="p-6 text-center text-zinc-400 italic">No audit events yet.</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-zinc-200" data-testid={`audit-${r.id}`}>
                <td className="p-3 text-xs font-mono whitespace-nowrap">{fmtDate(r.created_at)}</td>
                <td className="p-3 text-xs">{r.actor_name}<div className="text-[10px] font-mono text-zinc-400">{r.actor_email}</div></td>
                <td className="p-3"><span className="text-[10px] font-bold uppercase tracking-wider bg-zinc-100 border border-zinc-300 px-1.5 py-0.5">{r.action}</span></td>
                <td className="p-3 text-xs">{r.target_label || r.target_id}</td>
                <td className="p-3 text-xs text-zinc-600">{r.note || ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ====== Impersonation audit — every support session recorded ====== */
function ImpersonationAuditPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/platform/impersonation-audit")
      .then((r) => setRows(r.data || []))
      .finally(() => setLoading(false));
  }, []);
  const activeCount = rows.filter((r) => !r.ended_at).length;
  const fmtDuration = (start, end) => {
    if (!end) return "active";
    const ms = new Date(end).getTime() - new Date(start).getTime();
    const m = Math.round(ms / 60000);
    if (m < 1) return "<1m";
    if (m < 60) return `${m}m`;
    return `${(m / 60).toFixed(1)}h`;
  };
  return (
    <div className="space-y-3" data-testid="impersonation-audit-panel">
      <div className="flex items-center gap-3">
        <div className="text-xs font-mono text-zinc-500 uppercase tracking-widest">
          {rows.length} sessions
        </div>
        {activeCount > 0 && (
          <div className="text-[10px] font-bold uppercase tracking-widest bg-[#FF5F15] text-white px-2 py-0.5" data-testid="active-sessions-count">
            {activeCount} active
          </div>
        )}
      </div>
      <div className="hard-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-100 text-[10px] font-bold uppercase tracking-wider text-zinc-700">
            <tr>
              <th className="text-left p-3">Started</th>
              <th className="text-left p-3">Duration</th>
              <th className="text-left p-3">Super admin</th>
              <th className="text-left p-3">Logged in as</th>
              <th className="text-left p-3">Org</th>
              <th className="text-left p-3">IP</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan="6" className="p-6 text-center text-zinc-400 italic">Loading…</td></tr>}
            {!loading && rows.length === 0 && (
              <tr><td colSpan="6" className="p-6 text-center text-zinc-400 italic">No impersonation sessions yet.</td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-zinc-200" data-testid={`imp-session-${r.id}`}>
                <td className="p-3 text-xs font-mono whitespace-nowrap">{fmtDate(r.started_at)}</td>
                <td className="p-3 text-xs font-mono">
                  {r.ended_at ? (
                    <span>{fmtDuration(r.started_at, r.ended_at)}</span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-[#FF5F15] font-bold uppercase tracking-widest text-[10px]">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#FF5F15] animate-pulse" /> active
                    </span>
                  )}
                </td>
                <td className="p-3 text-xs">
                  {r.super_admin_name}
                  <div className="text-[10px] font-mono text-zinc-400">{r.super_admin_email}</div>
                </td>
                <td className="p-3 text-xs">
                  {r.target_user_name}
                  <div className="text-[10px] font-mono text-zinc-400">{r.target_user_email}</div>
                </td>
                <td className="p-3 text-xs font-bold">{r.target_org_name}</td>
                <td className="p-3 text-[10px] font-mono text-zinc-500">{r.ip || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
function SupportPanel() {
  const [tickets, setTickets] = useState([]);
  const [filter, setFilter] = useState("open");
  const [open, setOpen] = useState(null);
  const load = () => api.get("/platform/support", { params: filter === "all" ? {} : { status: filter } }).then((r) => setTickets(r.data));
  useEffect(() => { load(); }, [filter]);
  return (
    <div className="space-y-4" data-testid="support-panel">
      <div className="flex gap-2">
        {["open", "closed", "all"].map((s) => (
          <Button key={s} size="sm" variant={filter === s ? "default" : "outline"}
            onClick={() => setFilter(s)}
            className={`rounded-sm uppercase tracking-wider font-bold text-xs ${filter === s ? "bg-zinc-900 text-white" : ""}`}
            data-testid={`support-filter-${s}`}>{s}</Button>
        ))}
      </div>
      <div className="hard-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-100 text-[10px] font-bold uppercase tracking-wider text-zinc-700">
            <tr>
              <th className="text-left p-3">Subject</th>
              <th className="text-left p-3">From</th>
              <th className="text-left p-3">Priority</th>
              <th className="text-left p-3">Status</th>
              <th className="text-left p-3">Created</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {tickets.length === 0 && <tr><td colSpan="6" className="p-6 text-center text-zinc-400 italic">No tickets.</td></tr>}
            {tickets.map((t) => (
              <tr key={t.id} className="border-t border-zinc-200 hover:bg-zinc-50 cursor-pointer" onClick={() => setOpen(t)} data-testid={`support-row-${t.id}`}>
                <td className="p-3 font-bold">{t.subject}</td>
                <td className="p-3 text-xs">{t.user_name}<div className="text-[10px] text-zinc-500 font-mono">{t.org_name}</div></td>
                <td className="p-3"><span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm ${t.priority === "urgent" ? "bg-red-600 text-white" : t.priority === "high" ? "bg-orange-100 text-orange-800" : "bg-zinc-100 text-zinc-700"}`}>{t.priority}</span></td>
                <td className="p-3"><span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm ${t.status === "open" ? "bg-blue-100 text-blue-800" : "bg-zinc-100 text-zinc-500"}`}>{t.status}</span></td>
                <td className="p-3 text-xs font-mono">{fmtDate(t.created_at)}</td>
                <td className="p-3 text-right"><ChevronRight className="w-4 h-4 text-zinc-400" /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <TicketDialog ticket={open} onClose={() => { setOpen(null); load(); }} />
    </div>
  );
}

function TicketDialog({ ticket, onClose }) {
  const [reply, setReply] = useState("");
  const [close, setClose] = useState(false);
  if (!ticket) return null;
  const send = async () => {
    if (!reply.trim()) return;
    await api.patch(`/platform/support/${ticket.id}`, { body: reply, close });
    toast.success(close ? "Replied & closed" : "Reply sent");
    setReply("");
    onClose();
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="rounded-sm max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display uppercase">{ticket.subject}</DialogTitle>
          <DialogDescription>From {ticket.user_name} · {ticket.org_name}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="p-3 bg-zinc-50 border border-zinc-300">
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">{fmtDate(ticket.created_at)}</div>
            <p className="text-sm whitespace-pre-wrap">{ticket.body}</p>
          </div>
          {(ticket.replies || []).map((r, i) => (
            <div key={i} className={`p-3 border ${r.is_staff ? "bg-[#FF5F15]/10 border-[#FF5F15]/30 ml-8" : "bg-zinc-50 border-zinc-300 mr-8"}`}>
              <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">
                {r.actor_name} · {fmtDate(r.created_at)}
              </div>
              <p className="text-sm whitespace-pre-wrap">{r.body}</p>
            </div>
          ))}
          {ticket.status === "open" && (
            <div className="space-y-2">
              <Textarea rows={4} value={reply} onChange={(e) => setReply(e.target.value)}
                placeholder="Type your reply…" className="rounded-sm border-zinc-400" data-testid="ticket-reply-input" />
              <label className="inline-flex items-center gap-2 text-xs">
                <input type="checkbox" checked={close} onChange={(e) => setClose(e.target.checked)} data-testid="ticket-close-checkbox" />
                <span className="font-bold uppercase tracking-wider">Mark closed after sending</span>
              </label>
            </div>
          )}
        </div>
        {ticket.status === "open" && (
          <DialogFooter>
            <Button onClick={send} disabled={!reply.trim()}
              className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] uppercase tracking-wider font-bold" data-testid="ticket-send">
              <Send className="w-4 h-4 mr-1" /> {close ? "Reply & close" : "Reply"}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ====== Broadcasts ====== */
function BroadcastPanel() {
  const [list, setList] = useState([]);
  const [form, setForm] = useState({ message: "", severity: "info", starts_at: "", ends_at: "" });
  const load = () => api.get("/platform/broadcasts").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);
  const create = async () => {
    if (!form.message.trim()) { toast.error("Message required"); return; }
    await api.post("/platform/broadcasts", {
      ...form, starts_at: form.starts_at || null, ends_at: form.ends_at || null,
    });
    setForm({ message: "", severity: "info", starts_at: "", ends_at: "" });
    toast.success("Broadcast sent");
    window.dispatchEvent(new CustomEvent("fl:broadcasts-changed"));
    load();
  };
  const del = async (b) => {
    if (!window.confirm("Delete this broadcast? It will disappear for everyone immediately.")) return;
    try {
      await api.delete(`/platform/broadcasts/${b.id}`);
      // Optimistic local remove + tell the global BroadcastBanner to refetch
      setList((arr) => arr.filter((x) => x.id !== b.id));
      window.dispatchEvent(new CustomEvent("fl:broadcasts-changed"));
      toast.success("Broadcast deleted");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete broadcast");
      load();
    }
  };
  return (
    <div className="space-y-4" data-testid="broadcast-panel">
      <div className="hard-card p-5 space-y-3">
        <div className="label-tag">Send a new broadcast</div>
        <Textarea rows={3} value={form.message} onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
          placeholder="Maintenance Sunday 2am — Fieldlink may be briefly offline."
          className="rounded-sm border-zinc-400" data-testid="broadcast-message" />
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Field label="Severity">
            <Select value={form.severity} onValueChange={(v) => setForm((f) => ({ ...f, severity: v }))}>
              <SelectTrigger className="rounded-sm border-zinc-400" data-testid="broadcast-severity"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="info">Info</SelectItem>
                <SelectItem value="warning">Warning</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Starts (optional)"><Input type="datetime-local" value={form.starts_at} onChange={(e) => setForm((f) => ({ ...f, starts_at: e.target.value }))} className="rounded-sm border-zinc-400" data-testid="broadcast-starts" /></Field>
          <Field label="Ends (optional)"><Input type="datetime-local" value={form.ends_at} onChange={(e) => setForm((f) => ({ ...f, ends_at: e.target.value }))} className="rounded-sm border-zinc-400" data-testid="broadcast-ends" /></Field>
        </div>
        <div className="flex justify-end">
          <Button onClick={create} className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] uppercase tracking-wider font-bold" data-testid="broadcast-send">
            <Megaphone className="w-4 h-4 mr-1.5" /> Send broadcast
          </Button>
        </div>
      </div>
      <div className="hard-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-100 text-[10px] font-bold uppercase tracking-wider text-zinc-700">
            <tr>
              <th className="text-left p-3">Message</th>
              <th className="text-left p-3">Severity</th>
              <th className="text-left p-3">Window</th>
              <th className="text-left p-3">Created</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 && <tr><td colSpan="5" className="p-6 text-center text-zinc-400 italic">No broadcasts yet.</td></tr>}
            {list.map((b) => (
              <tr key={b.id} className="border-t border-zinc-200" data-testid={`broadcast-row-${b.id}`}>
                <td className="p-3"><div className="font-bold">{b.message}</div></td>
                <td className="p-3"><span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm ${b.severity === "critical" ? "bg-red-600 text-white" : b.severity === "warning" ? "bg-yellow-100 text-yellow-800" : "bg-blue-100 text-blue-800"}`}>{b.severity}</span></td>
                <td className="p-3 text-xs font-mono">{b.starts_at ? fmtDate(b.starts_at) : "—"}<br />{b.ends_at ? fmtDate(b.ends_at) : "—"}</td>
                <td className="p-3 text-xs font-mono">{fmtDate(b.created_at)}</td>
                <td className="p-3 text-right">
                  <Button size="sm" variant="ghost" onClick={() => del(b)} className="text-zinc-500 hover:text-red-600" data-testid={`broadcast-delete-${b.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


// ============================================================
// AI Ads — generate marketing videos with Sora 2 + OpenAI TTS.
// Super Admin only. Each generation costs Universal Key credits.
// ============================================================
function AdsPanel() {
  const [scripts, setScripts] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [pickedScript, setPickedScript] = useState("");
  const [includeVo, setIncludeVo] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const loadJobs = async () => {
    const { data } = await api.get("/platform/ads");
    setJobs(data);
  };

  useEffect(() => {
    api.get("/platform/ads/scripts").then((r) => {
      setScripts(r.data);
      if (r.data.length && !pickedScript) setPickedScript(r.data[0].id);
    }).catch(() => {});
    loadJobs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Poll while any job is in flight
  const hasInflight = useMemo(
    () => jobs.some((j) => !["done", "failed"].includes(j.status)),
    [jobs],
  );
  useEffect(() => {
    if (!hasInflight) return;
    const t = setInterval(loadJobs, 8000);
    return () => clearInterval(t);
  }, [hasInflight]);

  const submit = async () => {
    if (!pickedScript) return;
    setSubmitting(true);
    try {
      await api.post("/platform/ads/generate", { script_id: pickedScript, include_voiceover: includeVo });
      toast.success("Generation started — usually 3-8 minutes");
      loadJobs();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (j) => {
    if (!window.confirm("Delete this ad and all its clips?")) return;
    try {
      await api.delete(`/platform/ads/${j.id}`);
      toast.success("Removed");
      loadJobs();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const pickedMeta = scripts.find((s) => s.id === pickedScript);

  return (
    <div className="space-y-6" data-testid="platform-ads-panel">
      {/* Generate panel */}
      <div className="hard-card p-5 bg-[#09090B] text-white">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="w-4 h-4 text-[#FF5F15]" />
          <span className="label-tag !text-[#FF5F15]">Generate a new ad</span>
        </div>
        <div className="grid sm:grid-cols-3 gap-3">
          <div className="sm:col-span-2">
            <Label className="text-[10px] uppercase tracking-widest text-zinc-400">Script</Label>
            <Select value={pickedScript} onValueChange={setPickedScript}>
              <SelectTrigger className="mt-2 rounded-sm border-zinc-700 bg-zinc-900 text-white"
                             data-testid="ads-script-select">
                <SelectValue placeholder="Pick a script" />
              </SelectTrigger>
              <SelectContent>
                {scripts.map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {pickedMeta && (
              <div className="mt-2 text-[10px] font-mono uppercase tracking-widest text-zinc-400">
                {pickedMeta.clip_count} × {pickedMeta.clip_duration}s clips ({pickedMeta.size}) · {pickedMeta.total_duration}s total
                {pickedMeta.has_voiceover && <> · voice-over available</>}
              </div>
            )}
          </div>
          <div className="flex flex-col justify-end">
            <label className="flex items-center gap-2 text-xs mb-3 cursor-pointer">
              <input type="checkbox"
                     checked={includeVo}
                     onChange={(e) => setIncludeVo(e.target.checked)}
                     disabled={!pickedMeta?.has_voiceover}
                     data-testid="ads-vo-toggle"
                     className="w-4 h-4 accent-[#FF5F15]" />
              <span className="uppercase tracking-wider font-bold text-zinc-200">
                Include voice-over
              </span>
            </label>
            <Button
              onClick={submit}
              disabled={submitting || !pickedScript}
              className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase tracking-wider font-bold"
              data-testid="ads-generate-button">
              {submitting ? "Queuing…" : "Generate"}
            </Button>
          </div>
        </div>
        <div className="mt-3 text-[10px] font-mono uppercase tracking-widest text-zinc-500 leading-relaxed">
          Uses your Universal Key. Roughly NZ$2–4 per 15-second ad. Generation runs in the
          background — refresh stays live. You'll get the raw clips to stitch together in CapCut.
        </div>
      </div>

      {/* Jobs list */}
      <div className="space-y-3" data-testid="ads-jobs-list">
        {jobs.length === 0 ? (
          <div className="text-sm text-zinc-500 text-center py-12 border-2 border-dashed border-zinc-200">
            No ads generated yet. Pick a script above to kick one off.
          </div>
        ) : (
          jobs.map((j) => <AdJobCard key={j.id} job={j} onDelete={() => remove(j)} onRefresh={loadJobs} />)
        )}
      </div>
    </div>
  );
}

function AdJobCard({ job, onDelete, onRefresh }) {
  const isDone = job.status === "done";
  const isFailed = job.status === "failed";
  const isWorking = !isDone && !isFailed;
  const tok = typeof window !== "undefined" ? localStorage.getItem("fl_token") || "" : "";
  const base = process.env.REACT_APP_BACKEND_URL || "";
  const fileUrl = (p) => `${base}/api/files/view?path=${encodeURIComponent(p)}&auth=${encodeURIComponent(tok)}`;

  return (
    <div className={`hard-card p-5 ${isFailed ? "border-2 border-red-400" : isDone ? "border-2 border-green-500" : "border-2 border-[#FF5F15]"}`}
         data-testid={`ad-job-${job.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-[10px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded-sm ${
              isDone ? "bg-green-600 text-white" :
              isFailed ? "bg-red-600 text-white" :
              "bg-[#FF5F15] text-white"
            }`}>
              {isWorking ? <RefreshCw className="w-3 h-3 inline mr-1 animate-spin" /> : null}
              {job.status.replace(/_/g, " ")}
            </span>
            <span className="font-display font-bold uppercase tracking-tight text-base">{job.script_name}</span>
          </div>
          <div className="mt-1 text-xs text-zinc-600">{job.progress_label}</div>
          {job.error && (
            <div className="mt-2 text-xs text-red-700 bg-red-50 border border-red-200 px-2 py-1 font-mono">
              {job.error}
            </div>
          )}
          <div className="mt-1 text-[10px] font-mono uppercase tracking-widest text-zinc-400">
            {fmtDate(job.created_at)} · by {job.created_by_name || "—"}
          </div>
        </div>
        <div className="flex items-center gap-1">
          {isWorking && (
            <Button size="icon" variant="outline" onClick={onRefresh}
                    className="h-8 w-8 rounded-sm" data-testid={`ad-job-refresh-${job.id}`}>
              <RefreshCw className="w-3.5 h-3.5" />
            </Button>
          )}
          <Button size="icon" variant="ghost" onClick={onDelete}
                  className="h-8 w-8 rounded-sm text-zinc-500 hover:text-red-600"
                  data-testid={`ad-job-delete-${job.id}`}>
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>

      {/* Clips */}
      {(job.clips?.length || 0) > 0 && (
        <div className="mt-4 grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid={`ad-job-clips-${job.id}`}>
          {job.clips.map((c) => (
            <div key={c.index} className="border-2 border-zinc-200 bg-zinc-50" data-testid={`ad-clip-${job.id}-${c.index}`}>
              <video
                src={fileUrl(c.storage_path)}
                controls
                playsInline
                className="w-full bg-black aspect-[9/16] object-contain"
              />
              <div className="p-2">
                <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-700 line-clamp-1">
                  {c.index}. {c.label}
                </div>
                <div className="text-[9px] font-mono text-zinc-400 mt-0.5">
                  {c.duration}s · {(c.size / 1024 / 1024).toFixed(1)} MB
                </div>
                <a href={fileUrl(c.storage_path)}
                   onClick={(e) => { e.preventDefault(); downloadFileAsBlob(fileUrl(c.storage_path), `clip_${c.index}.mp4`); }}
                   className="mt-1.5 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-[#FF5F15] hover:underline cursor-pointer"
                   data-testid={`ad-clip-download-${job.id}-${c.index}`}>
                  <Download className="w-3 h-3" /> MP4
                </a>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Voice-over */}
      {job.voiceover && (
        <div className="mt-3 border-2 border-blue-200 bg-blue-50 p-3" data-testid={`ad-vo-${job.id}`}>
          <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
            <span className="text-[10px] font-bold uppercase tracking-widest text-blue-800">Voice-over · {job.voiceover.voice}</span>
            <a href={fileUrl(job.voiceover.storage_path)}
               onClick={(e) => { e.preventDefault(); downloadFileAsBlob(fileUrl(job.voiceover.storage_path), "voiceover.mp3"); }}
               className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-blue-800 hover:underline cursor-pointer"
               data-testid={`ad-vo-download-${job.id}`}>
              <Download className="w-3 h-3" /> MP3
            </a>
          </div>
          <audio src={fileUrl(job.voiceover.storage_path)} controls className="w-full h-8" />
          <div className="mt-2 text-[10px] font-mono text-blue-900 leading-relaxed italic">"{job.voiceover.text}"</div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// Brand assets — logos, cover photos, social images. Super Admin downloads
// these for Facebook / LinkedIn / Instagram. AI-generated via Nano Banana.
// ============================================================
function BrandPanel() {
  const [items, setItems] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [genOpen, setGenOpen] = useState(false);

  const load = async () => {
    const { data } = await api.get("/platform/brand_assets");
    setItems(data);
  };

  useEffect(() => {
    Promise.all([
      api.get("/platform/brand_assets").then((r) => setItems(r.data)),
      api.get("/platform/brand_assets/templates").then((r) => setTemplates(r.data)),
    ])
      .catch(() => toast.error("Failed to load brand assets"))
      .finally(() => setLoading(false));
  }, []);

  const tok = typeof window !== "undefined" ? localStorage.getItem("fl_token") || "" : "";
  const base = process.env.REACT_APP_BACKEND_URL || "";
  const fileUrl = (p) => `${base}/api/files/view?path=${encodeURIComponent(p)}&auth=${encodeURIComponent(tok)}`;

  // Friendly title from the storage filename
  const titleOf = (filename) => {
    const stem = filename.replace(/\.(png|jpe?g|webp)$/i, "");
    const parts = stem.split(/[_-]/).filter(Boolean);
    return parts.map((w) => w[0].toUpperCase() + w.slice(1)).join(" ");
  };

  const groupOf = (filename) => {
    if (filename.startsWith("profile_")) return "Profile photos · square";
    if (filename.startsWith("cover_") && filename.includes("desktop")) return "Cover photos · desktop 2.62:1";
    if (filename.startsWith("cover_")) return "Cover photos · mobile 16:9";
    if (filename.startsWith("ig_")) return "Instagram posts";
    if (filename.startsWith("linkedin")) return "LinkedIn banners";
    if (filename.startsWith("twitter") || filename.startsWith("x_")) return "Twitter / X headers";
    return "Custom · other";
  };

  const grouped = useMemo(() => {
    const out = {};
    for (const it of items) {
      const g = groupOf(it.original_filename || it.storage_path.split("/").pop());
      (out[g] = out[g] || []).push(it);
    }
    return out;
  }, [items]);

  const remove = async (f) => {
    if (!window.confirm(`Delete ${f.original_filename}?`)) return;
    try {
      await api.delete(`/platform/brand_assets/${f.id}`);
      toast.success("Removed");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  if (loading) {
    return <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Loading…</div>;
  }

  return (
    <div className="space-y-6" data-testid="platform-brand-panel">
      <div className="hard-card p-5 bg-[#09090B] text-white flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-2">
            <Image className="w-4 h-4 text-[#FF5F15]" />
            <span className="label-tag !text-[#FF5F15]">Brand assets</span>
          </div>
          <p className="text-xs text-zinc-400 leading-relaxed max-w-2xl">
            Ready-to-upload profile photos and cover banners for your Facebook
            / LinkedIn / Instagram pages. Click any image to preview at full
            size, click <b>Download</b> to save the original PNG, or generate
            fresh variants with one click.
          </p>
        </div>
        <Button
          onClick={() => setGenOpen(true)}
          className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase tracking-wider font-bold"
          data-testid="brand-generate-button">
          <Sparkles className="w-4 h-4 mr-2" /> Generate new
        </Button>
      </div>

      {items.length === 0 ? (
        <div className="text-sm text-zinc-500 text-center py-12 border-2 border-dashed border-zinc-200">
          No brand assets yet — click <b>Generate new</b> above to make one.
        </div>
      ) : (
        Object.entries(grouped).map(([groupName, list]) => (
          <div key={groupName} data-testid={`brand-group-${groupName.replace(/[^a-z]/gi, "-").toLowerCase()}`}>
            <div className="label-tag mb-3">{groupName}</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {list.map((f) => (
                <BrandAssetCard
                  key={f.id}
                  asset={f}
                  fileUrl={fileUrl}
                  title={titleOf(f.original_filename || f.storage_path.split("/").pop())}
                  onDelete={() => remove(f)}
                />
              ))}
            </div>
          </div>
        ))
      )}

      {genOpen && (
        <BrandGenerateDialog
          templates={templates}
          onClose={() => setGenOpen(false)}
          onCreated={() => { setGenOpen(false); load(); }}
        />
      )}
    </div>
  );
}

function BrandAssetCard({ asset, fileUrl, title, onDelete }) {
  const url = fileUrl(asset.storage_path);
  const filename = asset.original_filename || asset.storage_path.split("/").pop();
  return (
    <div className="hard-card overflow-hidden" data-testid={`brand-asset-${filename}`}>
      <a href={url} target="_blank" rel="noreferrer" className="block bg-zinc-100 border-b border-zinc-200">
        <img
          src={url}
          alt={title}
          loading="lazy"
          className="w-full h-auto max-h-72 object-contain bg-[#09090B]"
        />
      </a>
      <div className="p-3 flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="font-display font-bold uppercase tracking-tight text-sm truncate">{title}</div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mt-0.5">
            {(asset.size / 1024).toFixed(0)} KB
          </div>
        </div>
        <div className="flex items-center gap-1">
          <a
            href={url}
            onClick={(e) => {
              e.preventDefault();
              downloadFileAsBlob(url, filename);
            }}
            className="inline-flex items-center gap-1 bg-[#FF5F15] text-white text-xs font-bold uppercase tracking-wider px-3 py-1.5 hover:bg-[#E65100] transition-colors cursor-pointer"
            data-testid={`brand-download-${filename}`}>
            <Download className="w-3.5 h-3.5" /> Download
          </a>
          <Button size="icon" variant="ghost"
                  onClick={onDelete}
                  className="h-8 w-8 rounded-sm text-zinc-500 hover:text-red-600"
                  data-testid={`brand-delete-${filename}`}>
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>
    </div>
  );
}

function BrandGenerateDialog({ templates, onClose, onCreated }) {
  const [mode, setMode] = useState("template");
  const [templateId, setTemplateId] = useState(templates[0]?.id || "");
  const [customPrompt, setCustomPrompt] = useState("");
  const [customName, setCustomName] = useState("");
  const [busy, setBusy] = useState(false);

  const pickedTemplate = templates.find((t) => t.id === templateId);

  const submit = async () => {
    setBusy(true);
    try {
      const body = mode === "template"
        ? { template_id: templateId }
        : { custom_prompt: customPrompt, custom_name: customName };
      await api.post("/platform/brand_assets/generate", body);
      toast.success("Generated!");
      onCreated();
    } catch (e) {
      const msg = e.response?.data?.detail || "Failed";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="rounded-sm max-w-xl max-h-[90vh] overflow-y-auto" data-testid="brand-generate-dialog">
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-tight text-2xl">Generate brand asset</DialogTitle>
          <DialogDescription>
            Uses Nano Banana via your Universal Key. Roughly $0.04 per image.
            Takes 10–30 seconds.
          </DialogDescription>
        </DialogHeader>

        {/* Mode tabs */}
        <div className="grid grid-cols-2 gap-1 bg-zinc-100 p-1 rounded-sm">
          <button
            type="button"
            onClick={() => setMode("template")}
            className={`text-xs font-bold uppercase tracking-wider py-2 ${
              mode === "template" ? "bg-[#09090B] text-white" : "text-zinc-600"
            }`}
            data-testid="brand-mode-template">
            Pre-made template
          </button>
          <button
            type="button"
            onClick={() => setMode("custom")}
            className={`text-xs font-bold uppercase tracking-wider py-2 ${
              mode === "custom" ? "bg-[#09090B] text-white" : "text-zinc-600"
            }`}
            data-testid="brand-mode-custom">
            Custom prompt
          </button>
        </div>

        {mode === "template" ? (
          <div className="space-y-3">
            <div>
              <Label className="label-tag">Template</Label>
              <Select value={templateId} onValueChange={setTemplateId}>
                <SelectTrigger className="mt-2 rounded-sm border-zinc-400" data-testid="brand-template-select">
                  <SelectValue placeholder="Pick a template" />
                </SelectTrigger>
                <SelectContent>
                  {templates.map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {pickedTemplate && (
                <div className="mt-2 text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                  {pickedTemplate.size_hint}
                </div>
              )}
            </div>
            {pickedTemplate && (
              <details className="text-xs">
                <summary className="cursor-pointer text-zinc-500 font-mono uppercase tracking-widest text-[10px] hover:text-zinc-900">
                  Show prompt
                </summary>
                <div className="mt-2 p-3 border border-zinc-200 bg-zinc-50 text-xs text-zinc-700 leading-relaxed font-mono whitespace-pre-wrap">
                  {pickedTemplate.prompt}
                </div>
              </details>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <Label className="label-tag">Friendly name <span className="text-zinc-400 normal-case">(used in filename)</span></Label>
              <Input value={customName} onChange={(e) => setCustomName(e.target.value)}
                     placeholder="e.g. xmas_sale_banner"
                     className="mt-2 rounded-sm border-zinc-400"
                     data-testid="brand-custom-name-input" />
            </div>
            <div>
              <Label className="label-tag">Prompt</Label>
              <Textarea
                rows={6}
                value={customPrompt}
                onChange={(e) => setCustomPrompt(e.target.value)}
                placeholder="Describe the image in detail. Mention aspect ratio (1:1 square / 16:9 horizontal) and brand colours (orange #FF5F15 and black #09090B)."
                className="mt-2 rounded-sm border-zinc-400 font-mono text-xs"
                data-testid="brand-custom-prompt-input" />
            </div>
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button type="button" variant="outline" onClick={onClose} className="rounded-sm" disabled={busy}>Cancel</Button>
          <Button onClick={submit}
                  disabled={busy || (mode === "custom" && customPrompt.trim().length < 10)}
                  className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase tracking-wider font-bold"
                  data-testid="brand-generate-submit">
            {busy ? "Generating… (~20s)" : <><Sparkles className="w-4 h-4 mr-2" />Generate</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
